/**
 * Het speelscherm van v1.
 *
 * Het bord staat op canvas (zie `board.ts`), alles eromheen — statusbalken, handkaarten,
 * knoppen, verslag — is gewone HTML. Dat leest beter, werkt met toetsenbord en
 * schermlezers, en scheelt werk.
 *
 * De regelkernel draait hier voorlopig in de browser. Zodra er accounts en online potjes
 * komen (fase 5) verhuist die naar de server; daarom leest dit scherm **uitsluitend** uit
 * `viewFor` en stuurt het alleen intenties terug.
 */
import {
  BASIC_CARDS,
  LANDSCAPE_RESOURCE,
  type Resource,
  type Seat,
} from '@catan/contracts';
import {
  aanvalsworpUit,
  bankRate,
  canAfford,
  handLimitOf,
  koopPrijsVoor,
  START_KAARTEN,
  roadSpots,
  settlementSpots,
  tradeAbilitiesOf,
  tradableWithBank,
  type Action,
  type GameView,
  type Payment,
  type Side,
  terugnameKandidaten,
  voorraadBouwwerken,
} from '@catan/rules';
import { afspeelplan, type Zichtbarezet } from './afspelen.js';
import type { Moment } from './tijdlijn.js';
import { markeringVoor } from './markering.js';
import { nieuwPotje, type Potje } from './lokaalspel.js';
import { maakOefenpotje, uitloggen, wieBenIkPreciezer } from './account.js';
import { netPotje } from './netpotje.js';
import { geenServerScherm, inlogscherm } from './inlogscherm.js';
import { installeerToetshaak } from './toetshaak.js';
import { hubscherm } from './hubscherm.js';
import { samenscherm } from './samenscherm.js';
import { KARAKTERS } from '@catan/contracts';
import type { Ik } from '@catan/contracts';
import {
  asset,
  hasAsset,
  playSound,
  setSoundEnabled,
  speelEnStop,
  speelVerhaal,
  stopVerhaal,
} from './assets.js';
import {
  bruikbareLandschappen,
  magAanwijzen,
  resterendAantal,
  resterendeKosten,
  vast,
  volledigBetaald,
  vrij,
  type Kosten,
} from './betalen.js';
import { Board, type Highlights } from './board.js';
import { figuurMeldingen, type Figuren, type Figuur, type FiguurMelding } from './figuren.js';
import {
  BEURTKLANK_MS,
  FX,
  isGeschud,
  opbrengstStappen,
  UI,
  volgendeBeurtklank,
  worpKlank,
} from './klanken.js';
import { Hud, type Bouwsoort } from './hud.js';
import {
  aanvalsWorp,
  grondstofVliegtNaar,
  grondstofWegvliegen,
  kaartInBeeld,
  stilleModus,
} from './animaties.js';
import {
  betaalPaneel,
  dialoog,
  eindScherm,
  eindTekening,
  gebeurtenisBanner,
  mededeling,
  plaatsingsPaneel,
  worpKeuze,
  type DialoogKnop,
} from './dialoog.js';
import { ruilPaneel, voorraadVan } from './ruilpaneel.js';
import { kaartBrowser, type BrowserKaart } from './kaartbrowser.js';
import { menuAchtergrond, nieuwSpel, titelAchtergrond } from './menu.js';

/**
 * De tegenstander die je in het opzetscherm koos.
 *
 * Geen constante meer: het origineel laat je kiezen uit acht figuren, elk met hun eigen
 * portret en beschrijving.
 */
// Het portret van de tegenstander zoekt de HUD zelf op aan de hand van zijn naam. Dat van
// jou niet: jouw naam en jouw speelfiguur zijn twee dingen, dus die houden we apart bij.
let TEGENSTANDER_NAAM = 'Marlene';

/**
 * Hoe scherp de tegenstander speelt: `Beginner`, `Gevorderd` of `Meester`.
 *
 * Dit ging jarenlang het menu in en er weer uit zonder ergens aan te komen. Nu bepaalt het
 * hoeveel ruis er op zijn afweging zit — zie `chooseAction` in `@catan/ai`.
 */
let MOEILIJKHEID = 'Gevorderd';

/** Welke van de acht figuren jij bent. Bepaalt alleen je portret, niet je naam. */
let EIGEN_FIGUUR = 'louis';

/**
 * Wie er is ingelogd, als er iemand is ingelogd.
 *
 * Nog niet nodig om te spelen: een potje tegen de computer draait in deze browser. Vanaf
 * het moment dat potjes op de server leven, is dit het begin van elke sessie.
 */
let ik: Ik | undefined;

/**
 * Zet de accountregel in de bedieningslaag.
 *
 * Alleen een mededeling. Hier stonden ooit de knoppen om in te loggen en samen te spelen —
 * de énige ingang naar die twee — in een lade die pas met de tab-toets in beeld schuift.
 * Wie met de muis speelde, vond ze nooit. Ze wonen nu op de voordeur en in de hub.
 */
function tekenAccount(): void {
  const vak = document.getElementById('account');
  if (vak === null) return;
  vak.innerHTML =
    ik === undefined
      ? '<p class="grijs">Niet ingelogd.</p>'
      : `<p class="grijs">Ingelogd als <strong>${ik.gebruikersnaam}</strong></p>`;
}

/**
 * Het scherm om samen te spelen, met eventueel een code die al vaststaat.
 *
 * Die code komt uit een gedeelde link: wie `/mee/ABC234` opent, hoeft niets over te typen.
 * Het pad zelf halen we daarna weg, zodat een keer verversen je niet opnieuw een kamer in
 * probeert te duwen die je net verlaten hebt.
 */
function opentSamenscherm(code?: string): void {
  samenscherm({
    figuren: KARAKTERS,
    ...(code === undefined ? {} : { code }),
    onPotje: (gameId, figuur) => {
      EIGEN_FIGUUR = figuur;
      hud.zetSpelerFiguur(figuur);
      void opentPotje(gameId);
    },
    onSluiten: () => undefined,
  });
}

/** De invitecode uit een gedeelde link, als er een in staat. */
function codeUitDeLink(): string | undefined {
  const gevonden = /^\/mee\/([A-Z2-9]{6})\/?$/i.exec(window.location.pathname);
  const code = gevonden?.[1];
  if (code === undefined) return undefined;
  window.history.replaceState(null, '', '/');
  return code.toUpperCase();
}

const cardById = new Map(BASIC_CARDS.map((c) => [c.id, c]));

/**
 * De weergave zoals het scherm hem nu kent.
 *
 * Ververst na elke zet en aan het begin van elke tekenbeurt. Het scherm leest **alleen**
 * hieruit: de volledige spelstand woont in `lokaalspel.ts` en komt hier niet.
 */
let view: GameView;

/** Haalt de nieuwste weergave op. Aanroepen na alles wat de stand verandert. */
/**
 * Welke stoel ben jij, en welke is de ander.
 *
 * Vroeger stond hier vast dat jij stoel 0 was, en tegen de computer klopt dat ook: die
 * krijgt stoel 1. Online niet. Wie een kamer maakt zit op stoel 0, wie meedoet op stoel 1,
 * en het hele scherm — je eigen kant van het bord, wiens beurt het is, wie een aanbod deed
 * — hing aan die aanname. De weergave weet het wél: `view.you.seat` is altijd jouw stoel.
 */
let JIJ: Seat = 0;
let COMPUTER: Seat = 1;

/**
 * Tot wanneer het scherm bezet is door een animatie.
 *
 * Een dobbelworp duurt ruim drie seconden en een kaart die groot in beeld komt nog eens
 * twee. Begint de computer ondertussen aan zijn beurt, dan rollen er twee dobbelstenen
 * door elkaar en verdwijnt jouw kaart achter de zijne. Alles wat wacht, wacht hierop.
 *
 * Staat hier bovenaan omdat `ververs()` al tijdens het laden draait en het beurtklankje
 * er meteen aanspraak op maakt.
 */
let bezetTot = 0;

/**
 * De afspeelrij-marker. Hier gedeclareerd — ver vóór zijn eigen logica — omdat
 * `beurtklank` hem leest en die al tijdens het láden draait: een `let` verderop in het
 * bestand is dan nog een dead zone en één aanraking breekt de hele opstart. Precies dat
 * gebeurde op 31 juli: de app kwam live niet meer voorbij het laadscherm.
 */
let afspelen: Promise<void> | undefined;

/**
 * De tijdlijn-opname voor de volgorde-toets: wat gebeurde er, in welke volgorde.
 *
 * Alleen met de toetshaak aan. De vlag staat statisch in de functie (puntnotatie — zelfde
 * les als in `toetshaak.ts`), dus in een productiebouw snoeit Vite het noteren weg en
 * blijft de lijst leeg. Hier bovenaan gedeclareerd om dezelfde reden als `afspelen`: er
 * wordt al tijdens het laden genoteerd, en een `let`/`const` verderop is dan een dead zone.
 */
const tijdlijnOpname: Moment[] = [];
function noteer(wat: string, detail?: string): void {
  if (import.meta.env.VITE_TOETSHAAK !== '1') return;
  // Een pot duurt de opname makkelijk uit; afkappen is beter dan een groeiende lijst in
  // een tabblad dat uren openstaat. De volgorde-toets kijkt toch alleen naar het begin.
  if (tijdlijnOpname.length >= 1000) return;
  tijdlijnOpname.push({ t: performance.now(), wat, ...(detail === undefined ? {} : { detail }) });
}

/** Meldt dat het scherm de komende `ms` bezet is. */
function bezet(ms: number): void {
  bezetTot = Math.max(bezetTot, performance.now() + ms);
}

/** Hoe lang de computer nog moet wachten voordat hij mag. */
function wachttijd(basis = 700): number {
  return Math.max(basis, bezetTot - performance.now() + 200);
}

function ververs(): void {
  view = spel.weergave();
  JIJ = view.you.seat;
  COMPUTER = view.opponent.seat;
  beurtklank();
}

/**
 * Waar het beurtklankje voor het laatst op stond. Zie `klanken.ts` voor het waarom.
 *
 * Het aanhaakpunt is `ververs()`: dat is de enige plek waar de weergave vernieuwt, dus
 * elke beurtwissel komt er langs, of hij nu van jou, van de computer of van de server
 * komt.
 */
let vorigeBeurtklank: Seat | undefined;

/** Hoeveel keer er in dit potje gegooid is — de twee worpklanken wisselen elkaar af. */
let worpen = 0;

/**
 * Staat de eerste vraag van een nieuw potje open?
 *
 * Zolang die openstaat is het bord alleen decor: het potje dat eronder ligt is het lege
 * potje van de hub en wordt weggegooid zodra je antwoordt. Zonder deze vlag begint de
 * computer daar vrolijk in te spelen terwijl jij nog naar de vraag kijkt.
 */
let wachtOpAntwoord = false;

/**
 * Wat het **bord** toont zolang een worp nog uitgespeeld wordt.
 *
 * De kern rekent een worp in één keer uit: de gebeurtenis én de opbrengst. Het scherm was
 * dus meteen bij: de grondstoffen stonden al op de landschappen terwijl de dobbelsteen nog
 * rolde, en je zág ze niet aankomen — het getal op de tegel sprong gewoon omhoog.
 *
 * Zolang dit gezet is tekent het bord hieruit in plaats van uit `view`. Het spel is dan al
 * verder; alleen het beeld loopt achter, en het haalt zichzelf in zodra de grondstoffen
 * neergekomen zijn.
 *
 * Alleen het bord. De knoppen en de toegankelijke lagen liepen hier ook op mee, en dat
 * maakte van elke worp een seconde waarin het scherm niet reageerde — een klik op
 * "Volgende" verdween dan in een knop die volgens het bevroren beeld niet bestond.
 */
let bevrorenStand: GameView | undefined;

/** Het korte stuk aan het begin van een beurt (`FX_005`), vlak vóór de worp. */
function beurtklank(): void {
  // Niet tijdens het afspelen: de stand zegt al "jij bent aan de beurt" terwijl de
  // computer op het scherm nog aan het bouwen is. Het slot van `zetInDeRij` roept dit
  // zélf aan zodra de rij leeg is — na de slotpauze, als zijn beurt ook echt af oogt.
  // Deze poort werkt alleen doordat beide aanleverpaden de rij vullen vóórdat ze
  // verversen; andersom klonk het klankje vóór het afspelen begon, en daarna nooit meer.
  if (afspelen !== undefined) return;
  const uit = volgendeBeurtklank(vorigeBeurtklank, view);
  vorigeBeurtklank = uit.stand;
  if (!uit.speel) return;
  noteer('beurtklank');
  playSound('effecten', FX.nieuweRonde);
  // Eerst de aankondiging, dán de worp — de stenen wachten tot het klankje uit is. Zonder
  // deze reservering rolt de dobbelsteen er na zeshonderd milliseconden dwars doorheen.
  bezet(BEURTKLANK_MS);
}

/**
 * Staat deze zet in de lijst die de kern doorgaf?
 *
 * Het scherm rekende dat vroeger zelf uit met `isLegalAction` en de volledige spelstand.
 * Nu vraagt het na. Betaling en doelwit blijven buiten beschouwing: de lijst bevat één
 * vorm per mogelijkheid, en het scherm vult de rest zelf in.
 */
function mag(type: Action['type'], gelijk?: Readonly<Record<string, unknown>>): boolean {
  return view.legalActions.some((a) => {
    if (a.type !== type) return false;
    if (gelijk === undefined) return true;
    const zet = a as unknown as Record<string, unknown>;
    return Object.entries(gelijk).every(([sleutel, waarde]) => zet[sleutel] === waarde);
  });
}

/**
 * Waar het scherm mee bezig is buiten de spelregels om.
 *
 * Betalen en ruilen zijn in dit spel geen knop maar een handeling: je wijst op je eigen
 * landschappen aan wélke grondstoffen eraf gaan. Dat is precies wat het origineel deed
 * ("Je betaalt de bouwkosten met een klik op een landschap met een grondstofsymbool",
 * handboek p. 14), en het maakt uit zodra je meer van een soort hebt dan je nodig hebt.
 */
/**
 * Waar je op dit moment voor afrekent.
 *
 * Vier dingen kosten grondstoffen, en ze horen alle vier hetzelfde te voelen: je wijst
 * zélf aan wat eraf gaat, je ziet het opstijgen en je hoort het. Twee ervan deden dat niet
 * — een stapel doorkijken en ruilen met de bank rekenden zichzelf af met de eerste
 * grondstoffen die ze tegenkwamen, en dan gebeurde er in beeld niets.
 */
type Betaaldoel =
  | 'straat'
  | 'dorp'
  | 'stad'
  /** Een uitbouwkaart uit je hand. */
  | { kaart: number }
  /** Twee grondstoffen om een stapel door te kijken. */
  | { stapel: number }
  /**
   * De koers van de bank, in de soort die je weggeeft.
   *
   * Staat `krijg` erbij, dan koos je in het ruilpaneel al wát je terugwilt en gaat de ruil
   * na het betalen meteen door. Anders wijs je zelf het landschap aan dat de nieuwe
   * grondstof krijgt.
   */
  | { bank: Resource; krijg?: Resource };

/** Hoe het doel heet in een zin als "betaal voor je …". */
function doelNaam(doel: Betaaldoel): string {
  if (typeof doel === 'string') return doel;
  if ('kaart' in doel) return cardById.get(doel.kaart)?.name ?? 'kaart';
  if ('stapel' in doel) return `stapel ${doel.stapel + 1}`;
  return `ruil met de bank`;
}

type Modus =
  | { kind: 'normaal' }
  /**
   * Je betaalt voor wat je gaat bouwen.
   *
   * Er staat nog geen plek bij: het origineel laat je **eerst afrekenen en daarna pas
   * aanwijzen**. Andersom kon je een plek kiezen die je niet kon betalen, en dan stond je
   * halverwege alsnog met lege handen.
   */
  | {
      kind: 'betalen';
      doel: Betaaldoel;
      kosten: Kosten;
      betaald: { column: number; side: Side }[];
    }
  | { kind: 'ruil-kies-soort' }
  | { kind: 'ruil-kies-doel'; give: Payment; soort: Resource }
  /** Betaald; nu wijs je op het bord aan waar het komt te staan. */
  | {
      kind: 'kies-bouwplek';
      bouw: 'straat' | 'dorp' | 'stad';
      payment: { column: number; side: Side }[];
    }
  /** Betaald; nu wijs je het bouwwerk aan waar de uitbouwkaart bij komt. */
  | { kind: 'kies-uitbouwplek'; cardId: number; payment: { column: number; side: Side }[] }
  /** Aan het eind van je beurt: van welke stapel pak je de bovenste? */
  | { kind: 'pak-stapel' }
  /** Aan het eind van je beurt: welke stapel koop je door? */
  | { kind: 'koop-stapel' }
  /** Aan het eind van je beurt: welke handkaart ruil je? */
  | { kind: 'ruil-handkaart' }
  /** Je koos welke kaart weggaat; nu wijs je de stapel aan waar hij onder verdwijnt. */
  | { kind: 'ruil-kies-stapel'; handIndex: number }
  /** Bij de opzet: je verdeelt je startlandschappen zelf. */
  | { kind: 'verdeel-landschappen'; gekozen?: number };

let modus: Modus = { kind: 'normaal' };

/**
 * Is dit een handeling waar je middenin kunt zitten als de wereld onder je verandert?
 *
 * Online kan de stand verschuiven terwijl jij aan het betalen bent: jouw ruilaanbod wordt
 * aangenomen, een aanbod verloopt. De betaallijst wijst dan naar grondstoffen die er niet
 * meer liggen, en afmaken levert alleen nog geweigerde zetten op. Zo'n handeling breken we
 * af met een melding — dat is jammer, maar vastzitten in een onmogelijke modus is erger.
 * De opzet (`verdeel-landschappen`) hoort hier níét bij: dat de ander ondertussen kiest is
 * daar juist de bedoeling.
 */
const HANDELING: ReadonlySet<Modus['kind']> = new Set([
  'betalen',
  'ruil-kies-soort',
  'ruil-kies-doel',
  'kies-bouwplek',
  'kies-uitbouwplek',
  'pak-stapel',
  'koop-stapel',
  'ruil-handkaart',
  'ruil-kies-stapel',
]);

/** Een dialoog die op antwoord wacht, of een banner die vanzelf verdwijnt. */
let overlay:
  | {
      soort: 'dialoog' | 'banner' | 'ruil' | 'browser' | 'plaatsen' | 'opzet' | 'einde' | 'opgeven';
      laag: import('pixi.js').Container;
    }
  | undefined;
/** De tekst van het openstaande plaatsingspaneel, om inhoudswissels op te merken. */
let plaatsenStempel = '';

function toonDialoog(vraag: string, knoppen: readonly DialoogKnop[]): void {
  noteer('vraag', vraag);
  sluitOverlay();
  const laag = dialoog({ vraag, knoppen });
  overlay = { soort: 'dialoog', laag };
  hud.vensters.addChild(laag);
  playSound('interface', UI.venster);
}

/**
 * Een dialoog die het einde van het potje aankondigt.
 *
 * Eigen soort, want gewone dialogen komen en gaan met het spel mee — deze juist niet, en
 * hij mag door geen enkele andere overlay verdrongen of tegengehouden worden.
 */
function toonEindeDialoog(vraag: string, knoppen: readonly DialoogKnop[]): void {
  sluitOverlay();
  const laag = dialoog({ vraag, knoppen });
  overlay = { soort: 'einde', laag };
  hud.vensters.addChild(laag);
  playSound('interface', UI.venster);
}

/**
 * Een mededeling op parkement, zoals "Het handelsaanbod is afgewezen!".
 *
 * Anders dan een banner hoort hier geen plaatje bij; hij zegt alleen wat er gebeurde en
 * verdwijnt daarna vanzelf.
 */
function toonMededeling(regel: string, ms = 1800): void {
  sluitOverlay();
  const laag = mededeling(regel);
  overlay = { soort: 'banner', laag };
  hud.vensters.addChild(laag);
  window.setTimeout(() => {
    if (overlay?.soort === 'banner') {
      sluitOverlay();
      render();
    }
  }, ms);
}

function toonBanner(gebeurtenis: string, uitleg: string, kaartId?: number): void {
  const laag = gebeurtenisBanner(gebeurtenis, uitleg, kaartId);
  if (laag === undefined) return;
  sluitOverlay();
  overlay = { soort: 'banner', laag };
  hud.vensters.addChild(laag);
  // Een banner verdwijnt vanzelf; een dialoog wacht op een antwoord. Daarna moet het
  // scherm opnieuw getekend worden: er kan een vraag klaarstaan die op zijn beurt wachtte.
  window.setTimeout(() => {
    if (overlay?.soort === 'banner') {
      sluitOverlay();
      render();
    }
  }, BANNER_MS);
}

/**
 * De tijdlijn van wat er na een zet in beeld komt.
 *
 * Het ging te snel: kaart, meteen banner, meteen door. Deze drie getallen staan hier bij
 * elkaar zodat het tempo op één plek te regelen is. `KAART_MS` moet meelopen met de duur
 * van `kaartInBeeld`, anders speelt de computer alweer terwijl jij nog leest.
 *
 * Wie beweging heeft uitgezet krijgt korte tijden. `animaties.ts` sloeg het bewegende deel
 * al over, maar deze wachttijden niet — dus bleef zo iemand alsnog vier seconden naar een
 * stilstaande kaart kijken. Dat is precies het tegenovergestelde van wat hij vroeg.
 *
 * De browsertoetsen zetten dezelfde voorkeur aan. Dat is geen truc voor de toetsen maar
 * dezelfde weg die een bezoeker kiest, en het scheelt minuten per suite.
 */
const stil = stilleModus();
const KAART_MS = stil ? 400 : 4200;
const BANNER_MS = stil ? 300 : 3200;
/** De adempauze tussen de kaart en de banner erna. */
const RUST_MS = stil ? 60 : 500;
/** Hoe lang de dobbelstenen rollen en blijven liggen, tot ze vervaagd zijn. */
const WORP_MS = stil ? 900 : 3400;
/**
 * Hoe lang een bouwwerk van de ander in beeld blijft flitsen.
 *
 * Ook deze hoort in de stille modus kort te zijn. Stond hier eerst vast op 1400: dan
 * blokkeerden vier bouwflitsen bijna vijf seconden, óók met beweging uit — en dat is precies
 * de tijd die een browsertoets niet heeft.
 */
const BOUW_MS = stil ? 200 : 1400;

const wacht = (ms: number): Promise<void> =>
  new Promise((klaar) => {
    window.setTimeout(klaar, ms);
  });

/**
 * De klank bij een speelfiguur die verschuift.
 *
 * `FX.BCF` heeft er vier eigen stukken voor — krijgen en verliezen, voor elk van de twee
 * figuren. Dat is precies wat deze banner nodig heeft. Eerder leende hij de muziek van de
 * gebeurtenissendobbelsteen, en `FX_004` (riddermacht gekregen) speelde ondertussen bij
 * élke bouwzet.
 */
const FIGUUR_KLANK: Readonly<Record<Figuur, { gekregen: string; verloren: string }>> = {
  riddermacht: { gekregen: FX.riddermachtGekregen, verloren: FX.riddermachtVerloren },
  handelsmacht: { gekregen: FX.handelsmachtGekregen, verloren: FX.handelsmachtVerloren },
};

/**
 * Een worp uitspelen, in de volgorde die het origineel aanhoudt.
 *
 * Eerst rollen de stenen uit. Dan de gebeurtenis: viel het vraagteken, dan komt de
 * getrokken kaart groot in beeld met zijn eigen muziek, en daarna vertelt de banner wat er
 * gebeurt. En **altijd als laatste** de opbrengst.
 *
 * Vroeger stonden deze stappen als losse afspraken naast elkaar — een `setTimeout` hier,
 * een `then` daar — en dat gaf precies de verkeerde volgorde: de grondstoffen stonden al
 * op de landschappen terwijl de steen nog rolde.
 */
async function speelWorpUit(
  worp: number,
  gebeurtenis: string,
  kaartId: number | undefined,
  zonderGevolg: boolean,
  voorZet: GameView,
): Promise<void> {
  await board.dobbelAnimatie(worp, gebeurtenis);
  render();

  const kaart = kaartId === undefined ? undefined : BASIC_CARDS.find((c) => c.id === kaartId);
  if (kaartId !== undefined) {
    bezet(KAART_MS);
    await kaartInBeeld(board.animaties, kaartId, kaart?.rules ?? '');
  }
  if (!zonderGevolg) {
    bezet(BANNER_MS + RUST_MS);
    toonBanner(gebeurtenis, kaart === undefined ? uitlegVan(gebeurtenis) : kaart.rules, kaartId);
    // En het stuk dat bij déze gebeurtenis hoort. Trok het vraagteken een kaart, dan heeft
    // die zijn eigen muziek al laten horen; die staat dan op de verhaalbaan en maakt hier
    // plaats. Het stuk mag uitspelen — de banner staat er drie seconden, het stuk zeven.
    const stuk = GEBEURTENIS_KLANK[gebeurtenis];
    if (stuk !== undefined) speelVerhaal('gebeurtenissen', stuk);
    await wacht(BANNER_MS);
  }

  // Staat er nog een keuze open — goed jaar, riddertoernooi, handelsvoordeel — dan houdt
  // de kern de opbrengst vast tot die keuze gemaakt is. Hier valt er dan niets te tonen en
  // maakt de tak bij `kies` het af.
  await laatOpbrengstAankomen(voorZet);
}

/**
 * De grondstoffen die erbij kwamen echt zien aankomen, en het bord daarna weer loslaten.
 *
 * Dit is de laatste stap van een beurt. Elke grondstof komt van boven op zijn landschap
 * gevallen met het grondstofgeluid erbij (`FX_007`, uit `grondstofVliegtNaar` zelf), en
 * pas als ze geland zijn gaat de bevriezing eraf — zodat het getal op de tegel omhooggaat
 * op het moment dat je de grondstof ziet neerkomen, en niet drie seconden eerder.
 *
 * Ze vliegen tegelijk. Een worp kan bij vier landschappen tegelijk raak zijn, en die één
 * voor één afwerken maakt van een halve seconde een beurt van drie.
 */
async function laatOpbrengstAankomen(voorZet: GameView): Promise<void> {
  const stappen = opbrengstStappen(voorZet, view);
  if (stappen.length > 0) {
    bezet(1000);
    const vluchten = stappen.flatMap((stap) => {
      const naar = board.schermPositie(stap.seat, stap.column, stap.side);
      const soort = LANDSCAPE_RESOURCE[stap.landschap];
      return Array.from({ length: stap.aantal }, () =>
        grondstofVliegtNaar(board.animaties, soort, { x: naar.x, y: naar.y - 90 }, naar),
      );
    });
    await Promise.all(vluchten);
  }
  bevrorenStand = undefined;
  render();
}

/** Wie de speelfiguren op dit moment heeft. */
function figurenNu(): Figuren {
  return { knight: view.knightToken, windmill: view.windmillToken };
}

/** Het vorstendom van een stoel. Van allebei zichtbaar: het ligt open op tafel. */
function vorstendom(seat: Seat): GameView['you']['principality'] {
  return seat === JIJ ? view.you.principality : view.opponent.principality;
}

/** Hoe deze speler heet. Jij heet voorlopig "Jij"; typen komt met de accounts. */
function spelerNaam(seat: Seat): string {
  return seat === JIJ ? view.you.name : view.opponent.name;
}

/**
 * De speelfiguren die zojuist verschoven, één voor één in beeld.
 *
 * Ze wachten op elkaar: wisselen ridder en molen in dezelfde zet, dan overschrijft de
 * tweede banner de eerste niet maar volgt hij erop. De stukken gaan op de verhaalbaan en
 * spelen dus uit — ze duren acht tot vijftien seconden en de banner maar drie. Wisselen
 * er twee figuren tegelijk, dan vaagt het eerste stuk weg als het tweede begint; dat is
 * precies wat die baan doet.
 */
async function toonFiguurBanners(meldingen: readonly FiguurMelding[]): Promise<void> {
  for (const melding of meldingen) {
    await wacht(RUST_MS);
    toonBanner(melding.figuur, melding.tekst);
    const klanken = FIGUUR_KLANK[melding.figuur];
    speelVerhaal(
      'effecten',
      melding.tekst.includes('kwijt') ? klanken.verloren : klanken.gekregen,
    );
    await wacht(BANNER_MS);
  }
}

/**
 * Het ruilscherm van het origineel: één paneel voor bank én tegenstander.
 *
 * Het paneel houdt zijn eigen invoer bij; wij krijgen alleen de uitkomst terug en zetten
 * die om in een zet. De bankruil moet daarbij van grondstofaantallen naar landschappen:
 * de regelkernel rekent in tegels, niet in getallen.
 */
/**
 * De voorraden zoals het openstaande ruilscherm ze toont.
 *
 * Het paneel werd één keer gebouwd en daarna nooit meer: liet je het openstaan terwijl er
 * gedobbeld of gebouwd werd, dan stonden er getallen van minuten geleden. Je bood dan een
 * baksteen aan die je niet meer had, of je kon er een vragen die de ander allang kwijt was.
 * Dit is de stand waarmee het paneel is getekend; wijkt hij af, dan tekent `render` hem
 * opnieuw.
 */
let ruilVoorraad: string | undefined;

/** Een korte samenvatting van beide voorraden, om te zien of er iets veranderd is. */
function voorraadStempel(): string {
  const tel = (seat: Seat): string =>
    Object.entries(voorraadVan(vorstendom(seat).landscapes))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([soort, n]) => `${soort}${n}`)
      .join('');
  return `${tel(JIJ)}|${tel(COMPUTER)}`;
}

function toonRuilpaneel(binnengekomen?: {
  give: Partial<Record<Resource, number>>;
  want: Partial<Record<Resource, number>>;
}): void {
  if (overlay?.soort === 'ruil') return;
  sluitOverlay();
  ruilVoorraad = voorraadStempel();

  const speler = view.you;
  const ander = view.opponent;
  const kunde = tradeAbilitiesOf(speler);
  const soorten = ['wol', 'erts', 'hout', 'goud', 'graan', 'klei'] as const;

  const laag = ruilPaneel({
    ...(binnengekomen === undefined ? {} : { binnengekomen }),
    onAntwoord: (accepteren) => {
      sluitOverlay();
      modus = { kind: 'normaal' };
      void doAction({ type: 'ruil-antwoord', accepteren });
    },
    regels: {
      voorraad: voorraadVan(speler.principality.landscapes),
      vanHen: voorraadVan(ander.principality.landscapes),
      koers: Object.fromEntries(soorten.map((s) => [s, bankRate(s, kunde)])) as Record<
        Resource,
        number
      >,
      tegenstander: TEGENSTANDER_NAAM,
    },
    onBank: (geef, aantal, krijg) => {
      const heb = speler.principality.landscapes
        .filter((t) => LANDSCAPE_RESOURCE[t.landscape] === geef)
        .reduce((som, t) => som + t.resources, 0);
      if (heb < aantal) {
        meld(`Je hebt ${aantal}× ${geef} nodig.`);
        return;
      }
      if (!speler.principality.landscapes.some(
        (t) => LANDSCAPE_RESOURCE[t.landscape] === krijg && t.resources < 3,
      )) {
        meld(`Je hebt geen ${krijg}-landschap met ruimte.`);
        return;
      }
      // Ook hier eerst afrekenen: het paneel koos vroeger zelf wélke drie hout eraf
      // gingen, en dan verdwenen ze zonder dat je ze zag of hoorde.
      modus = {
        kind: 'betalen',
        doel: { bank: geef, krijg },
        kosten: vast({ [geef]: aantal }),
        betaald: [],
      };
      sluitOverlay();
      meld(`Betaal ${aantal}× ${geef}: klik de grondstoffen aan.`);
      render();
    },
    onAanbod: (geef, wil) => {
      const zet: Action = { type: 'ruil-aanbod', give: geef, want: wil };
      modus = { kind: 'normaal' };
      sluitOverlay();
      void doAction(zet);
    },
    onSluiten: () => {
      modus = { kind: 'normaal' };
      sluitOverlay();
      render();
    },
  });
  overlay = { soort: 'ruil', laag };
  hud.vensters.addChild(laag);
}

/**
 * Het paneel tijdens het zelf verdelen van je startlandschappen.
 *
 * Meer dan een aanwijzing en een knop is het niet: het verdelen zelf gebeurt op het bord,
 * waar je een landschap aanklikt en daarna de plek waar het heen moet.
 */
/**
 * Legt de rest van je startlandschappen vanzelf neer — de knop "Verdeel voor mij".
 *
 * Geen aparte kernzet: het zijn gewoon de `plaats-landschap`-zetten die je ook zelf had
 * kunnen doen, in volgorde op de vrije plekken. Daardoor werkt hij overal — solo én op de
 * server — en blijft de herafspeelbaarheid vanzelf kloppen.
 */
let verdeeltVanzelf = false;
async function verdeelVanzelf(): Promise<void> {
  if (verdeeltVanzelf) return;
  verdeeltVanzelf = true;
  try {
    for (let stap = 0; stap < 6 && view.setupPool.length > 0; stap++) {
      const bezet = new Set(
        view.you.principality.landscapes.map((t) => `${t.column}:${t.side}`),
      );
      const plek = STARTPLEKKEN.find((p) => !bezet.has(`${p.column}:${p.side}`));
      if (plek === undefined) break;
      await doAction({ type: 'plaats-landschap', poolIndex: 0, column: plek.column, side: plek.side });
    }
  } finally {
    verdeeltVanzelf = false;
  }
  render();
}

function toonVerdeelpaneel(): void {
  // Wel elke keer opnieuw: de tekst verandert zodra er iets is neergelegd.
  sluitOverlay();
  const voorraad = view.setupPool;
  const eerstvolgende = voorraad[0];
  const alles = voorraad.length === 0;
  const kaarten = view.you.hand.length;

  const laag = plaatsingsPaneel({
    ...(eerstvolgende === undefined
      ? {}
      : { beeld: { groep: 'landschappen', naam: eerstvolgende.landscape } }),
    tekst: !alles
      ? `Leg je ${eerstvolgende?.landscape ?? 'landschap'} neer (${voorraad.length} te gaan).`
      : kaarten < START_KAARTEN
        ? 'Klik links een stapel aan voor je drie startkaarten.'
        : 'Alles ligt klaar. Klik op Klaar om te beginnen.',
    ...(alles && kaarten >= START_KAARTEN
      ? { knopSoort: 'ok' as const, knopLabel: 'Klaar' }
      : {}),
    ...(!alles
      ? {
          tweedeSoort: 'ok' as const,
          tweedeLabel: 'Verdeel voor mij',
          onTweede: () => {
            sluitOverlay();
            void verdeelVanzelf();
          },
        }
      : {}),
    onAnnuleer: () => {
      sluitOverlay();
      if (alles && kaarten >= START_KAARTEN) {
        modus = { kind: 'normaal' };
        // Mag het nog niet — de ander is nog bezig — dan sturen we het ook niet. Iets
        // versturen waarvan je wéét dat het geweigerd wordt, levert alleen een fout in de
        // console op. De wens blijft staan en gaat vanzelf de deur uit.
        if (mag('opzet-klaar')) {
          void doAction({ type: 'opzet-klaar' });
        } else {
          opzetKlaarGewenst = true;
          meld('Je bent klaar. We wachten nog even op je tegenstander.');
          render();
        }
        return;
      }
      if (!alles) {
        if (huidigPotjeId !== undefined) {
          // Dit potje staat op de server, misschien met een mens erin. "Annuleren" mag
          // dan nooit betekenen: gooi het weg en begin hier een potje tegen de computer —
          // dat deed het ooit wel, en dan zat je tegenstander op niemand te wachten.
          weiger('Leg eerst je landschappen neer; dit potje loopt al.');
        } else {
          // Nog niet alles ligt: dan verdeelt het spel de rest, want half beginnen kan niet.
          meld('De rest van je landschappen wordt voor je verdeeld.');
          modus = { kind: 'normaal' };
          beginPotje(spel.seed, false);
        }
      }
      render();
    },
  });
  overlay = { soort: 'opzet', laag };
  hud.vensters.addChild(laag);
}

/**
 * De muziek van het hoofdmenu.
 *
 * `cards_011` bleek bij het afluisteren geen kaartklank maar het menustuk van het
 * origineel — een van de tweeënveertig stukken in `cards.BCF` die in geen enkel gespeeld
 * potje voorbijkwamen. Hij loopt zolang je in de menu's bent en stopt zodra het potje
 * begint.
 */
let menuMuziek: (() => void) | undefined;

/** Het lange stuk onder het eindscherm; vaagt weg zodra je verder klikt. */
let eindMuziek: ((fadeMs?: number) => void) | undefined;

/** Zet het juiste volledige scherm neer, of haalt het weg als je speelt. */
function tekenScherm(): void {
  schermLaag?.destroy({ children: true });
  schermLaag = undefined;
  if (scherm === 'spel') {
    menuMuziek?.();
    menuMuziek = undefined;
    return;
  }
  if (menuMuziek === undefined) menuMuziek = speelEnStop('kaarten', 'cards_011');

  const laag =
    scherm === 'inlog'
      ? titelAchtergrond()
      : scherm === 'hub'
        ? menuAchtergrond('mainmenu_000')
        : nieuwSpel({
            ...(ik === undefined ? {} : { naam: ik.gebruikersnaam }),
            onStart: (keuze) => {
              TEGENSTANDER_NAAM = keuze.tegenstander.naam;
              MOEILIJKHEID = keuze.moeilijkheid;
              EIGEN_FIGUUR = keuze.figuur.portret;
              hud.zetSpelerFiguur(EIGEN_FIGUUR);
              // Alles is gekozen. Nu komt de eerste vraag van het potje zelf, en pas op
              // het antwoord ontstaat er een potje — precies één, en meteen het echte.
              vraagStartlandschappen();
            },
            onTerug: () => {
              naarHub();
            },
          });
  schermLaag = laag;
  hud.vensters.addChild(laag);
  // Staat er een venster open, dan hoort dat erbovenop te blijven. Zonder deze regel legt
  // een hertekening de menuplaat over een openstaande vraag heen, en dan zit je vast te
  // kijken naar een scherm dat op een antwoord wacht dat je niet meer kunt geven.
  if (overlay !== undefined) hud.vensters.addChild(overlay.laag);
}

/**
 * De zes plekken waar een startlandschap kan liggen.
 *
 * Diagonaal rond de twee dorpen (handboek p. 10). Dezelfde lijst als in de regelkern;
 * hier alleen om te tekenen welke nog leeg zijn.
 */
const STARTPLEKKEN: readonly { column: number; side: Side }[] = [
  { column: 0, side: 'boven' },
  { column: 2, side: 'boven' },
  { column: 4, side: 'boven' },
  { column: 0, side: 'onder' },
  { column: 2, side: 'onder' },
  { column: 4, side: 'onder' },
];

/** Hoeveel grondstoffen er in totaal op je landschappen liggen. */
function totaleVoorraad(landschappen: readonly { resources: number }[]): number {
  return landschappen.reduce((som, t) => som + t.resources, 0);
}

/**
 * De vraag waarmee elke beurt afsluit.
 *
 * Twee vormen, precies zoals de cd ze stelt. Heb je ruimte in je hand, dan mag je gratis
 * de bovenste kaart van een stapel nemen óf twee grondstoffen betalen om er een uit te
 * kiezen. Is je hand vol, dan mag je nog één kaart ruilen — en anders sluit je af.
 */
function toonKaartvraag(): void {
  const speler = view.you;
  const ruimte = speler.hand.length < handLimitOf(speler);
  const stapelMet = view.stackSizes.findIndex((hoogte) => hoogte > 0);
  if (stapelMet < 0) {
    void doAction({ type: 'beurt-beeindigen' });
    return;
  }

  if (ruimte) {
    toonDialoog('Bovenste kaart nemen, of een nieuwe kaart kopen?', [
      {
        soort: 'ja',
        label: 'Bovenste',
        onClick: () => {
          sluitOverlay();
          modus = { kind: 'pak-stapel' };
          meld('Klik links de stapel aan waar je de bovenste kaart van pakt.');
          render();
        },
      },
      {
        soort: 'bank',
        label: 'Kopen',
        onClick: () => {
          sluitOverlay();
          modus = { kind: 'koop-stapel' };
          meld(
            `Klik de stapel aan die je wilt doorkijken (${koopPrijsVoor(view.you)} grondstoffen).`,
          );
          render();
        },
      },
      {
        soort: 'nee',
        label: 'Klaar',
        onClick: () => {
          sluitOverlay();
          void doAction({ type: 'beurt-beeindigen' });
        },
      },
    ]);
    return;
  }

  // "…mag hij één ontwikkelingskaart ruilen" (p. 10): is die ene ruil gedaan, dan valt
  // er niets meer te vragen en is de volgende gewoon aan de beurt.
  if (!mag('ruil-af')) {
    void doAction({ type: 'beurt-beeindigen' });
    return;
  }
  toonDialoog('Wil je een kaart ruilen?', [
    {
      soort: 'ja',
      onClick: () => {
        sluitOverlay();
        modus = { kind: 'ruil-handkaart' };
        meld('Klik de handkaart aan die je wilt ruilen.');
        render();
      },
    },
    {
      soort: 'nee',
      onClick: () => {
        sluitOverlay();
        void doAction({ type: 'beurt-beeindigen' });
      },
    },
  ]);
}

/**
 * Eén kaart voor de browser, mét kostenstrook.
 *
 * Alle browservensters die echte kaarten tonen bouwen hun rijen hiermee, zodat de
 * kosten overal meekomen — juist in de stapelbrowser kies je wat je zo gaat betalen.
 */
function browserKaart(id: number): BrowserKaart {
  const kaart = cardById.get(id);
  return {
    id,
    naam: kaart?.name ?? `kaart ${id}`,
    ...(kaart?.cost === undefined ? {} : { kosten: kaart.cost }),
  };
}

/** De stapel die je hebt doorgekocht, om er een kaart uit te kiezen. */
function toonBrowser(stapel: number, titel?: string): void {
  sluitOverlay();
  const kaarten = (view.openStapel?.stack === stapel ? view.openStapel.kaarten : []).map(
    browserKaart,
  );
  const laag = kaartBrowser({
    kaarten,
    titel: titel ?? `Stapel ${stapel + 1} — kies een kaart`,
    onKies: (plek) => {
      sluitOverlay();
      void doAction({ type: 'kies', column: plek, side: 'boven' });
    },
    // Je hebt al betaald; sluiten zou je die grondstoffen kosten zonder kaart. Daarom
    // pakken we dan gewoon de bovenste — dat is nog altijd beter dan niets.
    onSluiten: () => {
      sluitOverlay();
      void doAction({ type: 'kies', column: 0, side: 'boven' });
    },
  });
  overlay = { soort: 'browser', laag };
  hud.vensters.addChild(laag);
}

/**
 * De landschapstapel doorbladeren met de Verkenner.
 *
 * "De speler mag alle kaarten van de landschapstapel bekijken en 2 kaarten uitkiezen"
 * (handboek p. 46). De volgorde is de plaatsing: eerst het landschap dat boven het nieuwe
 * dorp komt, dan dat eronder. Sluiten zonder te kiezen kan niet — je hébt de kaart al
 * ingezet, dus dan pakken we de bovenste.
 */
function toonLandschapKeuze(zijde: Side): void {
  sluitOverlay();
  // De stapel komt alleen mee zolang de Verkenner ernaar vraagt — daarbuiten mag je hem
  // niet zien, en dan valt er ook niets te kiezen.
  const kaarten = (view.landscapeDeck ?? []).map((kaart, i) => ({
    id: i,
    naam: `${kaart.landscape} ${kaart.dieNumber}`,
    groep: 'landschappen',
    plaat: kaart.landscape,
  }));
  const laag = kaartBrowser({
    kaarten,
    titel: `Verkenner — kies je ${zijde === 'boven' ? 'eerste' : 'tweede'} land`,
    onKies: (plek) => {
      sluitOverlay();
      void doAction({ type: 'kies', column: plek, side: 'boven' });
    },
    onSluiten: () => {
      sluitOverlay();
      void doAction({ type: 'kies', column: 0, side: 'boven' });
    },
  });
  overlay = { soort: 'browser', laag };
  hud.vensters.addChild(laag);
}

/**
 * De Spion legt de hand van je tegenstander open zodat je zelf kunt kiezen.
 *
 * "Kies uit de handkaarten van je tegenstander 1 kaart uit" — dat kán alleen als je ze
 * ziet, dus zolang deze vraag openstaat mag je meekijken.
 */
function toonSpion(van: Seat): void {
  sluitOverlay();
  const kaarten = (van === JIJ ? view.you.hand : (view.opponent.hand ?? [])).map(browserKaart);
  const laag = kaartBrowser({
    kaarten,
    titel: `Spion — kies een kaart van ${TEGENSTANDER_NAAM}`,
    onKies: (plek) => {
      sluitOverlay();
      void doAction({ type: 'kies', column: plek, side: 'boven' });
    },
    // "Er moet een kaart gestolen worden!" — wegklikken kan dus niet zomaar.
    onSluiten: () => {
      sluitOverlay();
      void doAction({ type: 'kies', column: 0, side: 'boven' });
    },
  });
  overlay = { soort: 'browser', laag };
  hud.vensters.addChild(laag);
}

/**
 * Het **Conflict**: de speler met de ridderfiguur kijkt in de hand van de ander.
 *
 * "De andere speler moet hem zijn hand tonen. De speler met de ridderfiguur zoekt er 2
 * kaarten uit, kiest een ontwikkelingsstapel en schuift ze eronder" (handboek p. 13 §15).
 *
 * Voor deze vraag bestond geen enkel scherm: hij viel terug op de algemene markering,
 * waardoor je eigen grondstofvelden gingen oplichten en de kern je klik terecht weigerde
 * met "daar staat geen ridder of vloot". Het potje liep daar vast.
 */
function toonConflict(van: Seat, resterend: number): void {
  sluitOverlay();
  const kaarten = (van === JIJ ? view.you.hand : (view.opponent.hand ?? [])).map(browserKaart);
  const laag = kaartBrowser({
    kaarten,
    titel: `Conflict — kies een kaart van ${TEGENSTANDER_NAAM} (nog ${resterend})`,
    onKies: (plek) => {
      sluitOverlay();
      void doAction({ type: 'kies', column: plek, side: 'boven' });
    },
    // Er móét gekozen worden; wegklikken zou het potje op deze vraag laten staan.
    onSluiten: () => {
      sluitOverlay();
      void doAction({ type: 'kies', column: 0, side: 'boven' });
    },
  });
  overlay = { soort: 'browser', laag };
  hud.vensters.addChild(laag);
}

/**
 * De **Bouwmeester**, laatste stap: een kaart uit je eigen hand teruggeven.
 *
 * "Iedere speler mag uit een uitbouwkaartenstapel een kaart uitkiezen en geeft er één voor
 * terug." Voor die stap bestond geen enkel UI-pad — je zat vast met vier kaarten in een
 * hand die er drie mag hebben, en geen manier om er een af te staan.
 */
function toonBouwmeesterAfgeven(): void {
  sluitOverlay();
  const kaarten = view.you.hand.map(browserKaart);
  const laag = kaartBrowser({
    kaarten,
    titel: 'Bouwmeester — welke kaart geef je terug?',
    onKies: (plek) => {
      sluitOverlay();
      void doAction({ type: 'kies', column: plek, side: 'boven' });
    },
    onSluiten: () => {
      sluitOverlay();
      void doAction({ type: 'kies', column: 0, side: 'boven' });
    },
  });
  overlay = { soort: 'browser', laag };
  hud.vensters.addChild(laag);
}

function sluitOverlay(): void {
  overlay?.laag.destroy();
  overlay = undefined;
}

/**
 * Waar je in de app bent.
 *
 * De app begint bij de voordeur: eerst inloggen, dan de hub, en pas daarna een spel.
 * `inlog`, `hub` en `opzet` zijn volledige schermen die het bord afdekken; `spel` is het
 * bord zelf. Het originele cd-hoofdmenu — acht knoppen, vijf dood — is vervallen; zijn
 * plaat leeft voort als decor onder de hub.
 */
type Scherm = 'inlog' | 'hub' | 'opzet' | 'spel';
let scherm: Scherm = 'inlog';

/**
 * Het HTML-paneel dat bij het huidige scherm hoort (voordeur of hub).
 *
 * Anders dan het canvas wordt HTML niet elke render opnieuw opgebouwd; het paneel leeft
 * tot je van scherm wisselt. `gaNaar` is de enige die deze twee variabelen aanraakt —
 * schermwissels op twee plekken regelen was precies hoe er eerder panelen bleven slingeren.
 */
let schermPaneel: (() => void) | undefined;

/** Een invitecode uit een /mee/-link, vastgehouden tot er iemand ingelogd is. */
let bewaardeCode: string | undefined;

/** Was de server onbereikbaar? Dan toont de voordeur dat in plaats van een formulier. */
let geenServer = false;

function gaNaar(nieuw: Scherm): void {
  schermPaneel?.();
  schermPaneel = undefined;
  scherm = nieuw;
  if (nieuw === 'inlog') schermPaneel = toonVoordeur();
  if (nieuw === 'hub') schermPaneel = toonHub();
  render();
}

/**
 * De voordeur: inloggen of met een code een account maken. Verplicht — er is geen "later".
 *
 * Is er helemaal geen server, dan is dat een eerlijke mededeling waard in plaats van een
 * formulier dat toch nergens heen kan.
 */
function toonVoordeur(melding?: string): () => void {
  if (geenServer) {
    return geenServerScherm({
      onOpnieuw: () => {
        geenServer = false;
        void wieBenIkPreciezer().then((wie) => {
          if (wie === 'geen-server') {
            geenServer = true;
            gaNaar('inlog');
            return;
          }
          ik = wie;
          tekenAccount();
          if (wie === undefined) gaNaar('inlog');
          else naarHub();
        });
      },
    });
  }
  return inlogscherm({
    doorzicht: true,
    ...(melding === undefined ? {} : { beginMelding: melding }),
    onIngelogd: (wie) => {
      ik = wie;
      tekenAccount();
      naarHub();
    },
  });
}

/**
 * Terug naar de hub — vanuit een afgelopen potje, na het inloggen, of na "sluiten".
 *
 * Het lopende potje wordt netjes losgelaten (de netpoll stopt) en vervangen door een
 * onzichtbaar lokaal potje, zodat de rest van de code nooit zonder `spel` zit.
 */
function naarHub(): void {
  // Muziek van een kaart of gebeurtenis mag uitspelen, maar niet in de hub doorlopen.
  stopVerhaal();
  wachtOpAntwoord = false;
  neemPotjeOver(start(nieuweSeed()));
  modus = { kind: 'normaal' };
  sluitOverlay();
  gaNaar('hub');
  // Lag er nog een invitecode uit een /mee/-link te wachten? Dan nu de lobby erbij.
  if (bewaardeCode !== undefined) {
    const code = bewaardeCode;
    bewaardeCode = undefined;
    opentSamenscherm(code);
  }
}

function toonHub(): () => void {
  if (ik === undefined) {
    // Hoort niet te kunnen; de voordeur is de enige weg hierheen. Maar mocht het toch —
    // een verlopen sessie precies op dit moment — dan is de voordeur het antwoord.
    window.setTimeout(() => gaNaar('inlog'), 0);
    return () => undefined;
  }
  return hubscherm({
    ik,
    onSolo: () => {
      gaNaar('opzet');
    },
    onSamen: () => {
      opentSamenscherm();
    },
    onPotje: (id) => {
      void opentPotje(id);
    },
    onUitloggen: () => {
      void uitloggen().then(() => {
        ik = undefined;
        tekenAccount();
        gaNaar('inlog');
      });
    },
  });
}
/**
 * Tot wanneer de berichtenbalk de worp nog niet mag verklappen.
 *
 * De regel "Jij gooit 3 en gebeurteniskaart" staat in het verslag zodra de zet gedaan
 * is — dus terwijl de stenen nog tollen. Dan hoef je niet meer te kijken. Zolang de
 * animatie loopt zegt de balk daarom alleen dát er gegooid wordt.
 */
let worpStilTot = 0;

let schermLaag: import('pixi.js').Container | undefined;

let spel: Potje = start(nieuweSeed());
// Meteen één keer ophalen: alles wat vóór de eerste tekenbeurt naar de weergave kijkt,
// moet er een vinden.
ververs();
let board: Board;
let hud: Hud;

/** Het vaste lot uit de URL (`?seed=…`), als er een gevraagd wordt. */
function vastLot(): string | undefined {
  const vast = new URLSearchParams(window.location.search).get('seed');
  return vast === null || vast === '' ? undefined : vast;
}

/**
 * Speelt deze bouw bewust in de browser in plaats van op de server?
 *
 * Alleen met de toetshaak erbij (`VITE_TOETSHAAK=1`) én `?lokaal=1` in de URL. Dat is er
 * voor de browsertoetsen die een spelstand klaarzetten: dat kan alleen op een potje dat
 * hier draait, en de haak weigert terecht een potje dat op de server staat. In een
 * productiebouw bestaat de haak niet en deze schakelaar dus ook niet.
 */
function lokaalGevraagd(): boolean {
  if (import.meta.env.VITE_TOETSHAAK !== '1') return false;
  return new URLSearchParams(window.location.search).get('lokaal') === '1';
}

function nieuweSeed(): string {
  // Een toets mag het lot vastleggen: met `?seed=...` in de URL is elk "nieuw" potje
  // hetzelfde potje. In gewoon gebruik staat die parameter er nooit, en het is bewust
  // alleen het sééd — geen stand, geen kaarten: alles verloopt verder volgens de regels.
  return vastLot() ?? `potje-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/**
 * Een nieuw potje.
 *
 * Standaard ligt je bord **leeg**: je zes landschappen zitten nog in je voorraad. Zo begin
 * je bij de vraag "wil je zelf verdelen?" met een schone tafel in plaats van met een
 * indeling die het spel alvast heeft neergelegd en meteen weer weghaalt als je "ja" zegt.
 * Antwoord je "nee", dan begint het potje opnieuw met `zelfVerdelen = false` en legt het
 * spel ze alsnog voor je neer.
 *
 * Het potje zelf wordt in `lokaalspel.ts` opgezet. Daar woont de spelstand; hier niet.
 *
 * Deze functie zet alleen op en geeft terug — ze raakt `spel` niet aan. Dat deed ze wel,
 * en dat brak de hele app: `let spel = start(...)` roept `start` aan terwijl `spel` nog
 * niet bestaat, dus de toewijzing erin liep tegen "Cannot access before initialization".
 * De aanroeper wijst toe en ververst; dat is één regel meer en één valkuil minder.
 */
function start(seed: string, zelfVerdelen = true): Potje {
  return nieuwPotje({
    seed,
    tegenstander: TEGENSTANDER_NAAM,
    moeilijkheid: MOEILIJKHEID,
    zelfVerdelen,
  });
}

/**
 * Zet een ander potje in beeld en laat het vorige netjes los.
 *
 * Een potje op de server vraagt elke twee seconden of de ander al iets gedaan heeft. Werd
 * het simpelweg overschreven, dan bleef die timer lopen — en zolang hij loopt meldt hij
 * ook dat jij er nog bent, zodat je tegenstander een potje dat jij allang verlaten hebt
 * nooit kan opgeven.
 */
function neemPotjeOver(nieuw: Potje): void {
  spel.sluit?.();
  huidigPotjeId = undefined;
  spel = nieuw;
  // De worpklanken beginnen weer bij de eerste; en het beurtklankje moet opnieuw kunnen
  // aanslaan, ook als het nieuwe potje toevallig bij dezelfde stoel begint.
  worpen = 0;
  vorigeBeurtklank = undefined;
  // Een halve film uit het vorige potje hoort niet over het nieuwe heen te lopen.
  afspeelrij = [];
  // Het portret van de tegenstander komt uit het nieuwe potje, of anders uit zijn naam. Laat
  // je de oude waarde staan, dan wint die van naam-matching en krijgt een computer­-
  // tegenstander het portret van de vorige menselijke speler.
  hud.zetTegenstanderFiguur(nieuw.tegenfiguur?.());
}

/** Een nieuw potje opzetten én in beeld brengen. */
function beginPotje(seed: string, zelfVerdelen = true): void {
  neemPotjeOver(start(seed, zelfVerdelen));
  ververs();
  // De fanfare van een nieuw potje. Staat hier en niet in `neemPotjeOver`, want dat wordt
  // ook gebruikt om het bord leeg achter te laten als je naar de hub teruggaat — en daar
  // begint niets.
  playSound('effecten', FX.nieuwSpel);
}

/**
 * Een potje beginnen: op de server als je ingelogd bent, anders hier.
 *
 * Het hoofdplan zegt "zonder inloggen speel je niet". Dat is voor een potje tússen mensen
 * onvermijdelijk, maar voor oefenen tegen de computer heb ik er bewust van afgeweken: het
 * pad in deze browser blijft bestaan zolang er geen sessie is. Dat kost geen tweede
 * spelregelcode — allebei de wegen draaien op dezelfde kern — en het scheelt een
 * toetshaak die op de server een spelstand kan neerzetten. Zo'n haak zou een veel groter
 * gat zijn dan dit een risico is.
 *
 * Wat je merkt van het verschil: ingelogd blijft je potje bewaard en kun je het op een
 * ander apparaat oppakken. Niet ingelogd is het weg zodra je het tabblad sluit.
 */
async function beginSoloPotje(zelfVerdelen: boolean): Promise<void> {
  schermPaneel?.();
  schermPaneel = undefined;
  huidigPotjeId = undefined;
  scherm = 'spel';
  // Rechtstreeks het échte potje. Hier stonden ooit twee potjes achter elkaar: eerst een
  // wegwerppotje als decor voor de vraag, en daarna pas het echte. De vraag komt nu vóór
  // het potje, dus er hoeft er maar één te bestaan.
  modus = zelfVerdelen ? { kind: 'verdeel-landschappen' } : { kind: 'normaal' };
  await startPotjeMet(zelfVerdelen);
  if (zelfVerdelen) meld('Kies een landschap uit je voorraad en klik de plek aan.');
  else meld('Klik links een stapel aan voor je drie startkaarten.');
  render();
}

/**
 * De eerste stap van een nieuw potje: leg je je startlandschappen zelf neer?
 *
 * In het origineel is dit geen instelling maar de eerste vraag die het spel je stelt, en
 * daar hoort hij ook: het is een manier van spelen, geen voorkeur die je vooraf invult.
 * Hij stond even als vierde keuzelijst in het opzetscherm; dat las als een formulier.
 *
 * De vraag komt vóór het potje bestaat. Dat is niet alleen netter maar ook veiliger: hij
 * heeft ooit ín een lopend potje gestaan, en toen maakte een antwoord een nieuw potje —
 * met als gevolg dat je in een partij tussen twee mensen stilzwijgend in je eigen,
 * andere spel terechtkwam.
 */
function vraagStartlandschappen(): void {
  // Het opzetscherm gaat eerst weg. Bleef het staan, dan hing de vraag over je eigen
  // portret en je keuzelijsten heen en leek het alsof er nog iets in te vullen viel.
  schermPaneel?.();
  schermPaneel = undefined;
  scherm = 'spel';
  modus = { kind: 'normaal' };
  wachtOpAntwoord = true;
  // Het bord eronder is het lege potje waarmee de hub het scherm gevuld hield, en dat zat
  // nog tegen de tegenstander van dáárvoor. Dan koos je Chi en keek je naar Marlene. Eén
  // vers decorpotje met de keuzes die je net maakte; het wordt weggegooid zodra je
  // antwoordt, want dan komt het echte.
  neemPotjeOver(start(nieuweSeed()));
  ververs();
  render();
  const antwoord = (zelf: boolean) => () => {
    wachtOpAntwoord = false;
    sluitOverlay();
    void beginSoloPotje(zelf);
  };
  toonDialoog('Wil je je startlandschappen zelf neerleggen?', [
    { soort: 'ja', onClick: antwoord(true) },
    { soort: 'nee', onClick: antwoord(false) },
  ]);
}

/** Het potje dat op de server draait, als dat er is. */
let huidigPotjeId: string | undefined;

/**
 * Je drukte op Klaar terwijl de ander nog bezig was.
 *
 * In de opzet richten beide spelers tegelijk hun vorstendom in, maar de fase gaat pas over
 * als ze allebei klaar zijn. Wie het eerst is, krijgt "je tegenstander is nog bezig" — en
 * dan bleef hij hangen op "Even wachten…", want de knop was inmiddels weg. Nu onthoudt het
 * scherm dat je klaar bent en zegt het alsnog zodra het mag.
 */
let opzetKlaarGewenst = false;

/**
 * De opgeefvraag is beantwoord (met ja of nee); niet blijven vragen.
 *
 * Bewust niet "de vraag is gesteld": een banner die eroverheen komt sluit de dialoog, en
 * dan hoort de vraag daarna gewoon terug te komen — een vraag die éénmalig verscheen en
 * door een mededeling werd weggedrukt, was er in de praktijk nooit.
 */
let opgeefBeantwoord = false;

/** Zegt alsnog "klaar" als dat inmiddels mag. Geeft terug of er iets verstuurd is. */
function probeerOpzetKlaar(): boolean {
  if (!opzetKlaarGewenst) return false;
  if (view.phase !== 'opzet') {
    opzetKlaarGewenst = false;
    return false;
  }
  if (!mag('opzet-klaar')) return false;
  opzetKlaarGewenst = false;
  void doAction({ type: 'opzet-klaar' });
  return true;
}

/**
 * Opent een potje dat op de server staat en brengt het in beeld.
 *
 * Ook de weg waarlangs een potje tussen twee mensen begint: de kamer meldt dat er gestart
 * is, en dan komt iedereen hier langs.
 */
async function opentPotje(gameId: string): Promise<void> {
  // Het paneel van het scherm waar je vandaan komt (hub of voordeur) gaat weg: het is
  // HTML en blijft anders over het bord heen staan — je ziet dan wel het spel, maar je
  // klikt op de hub.
  schermPaneel?.();
  schermPaneel = undefined;
  try {
    const nieuw = await netPotje({
      gameId,
      figuur: EIGEN_FIGUUR,
      // Zit er een mens tegenover je, dan doet die zijn zet terwijl jij niets doet. Zonder
      // dit zou je scherm blijven staan tot je zelf iets aanklikt.
      onNieuweStand: (zetten, gat) => {
        const modusVoor = modus.kind;
        // De stand van vóór het verversen vastleggen: dat is waarop het afspelen bevriest,
        // en waartegen de opbrengst straks wordt afgezet.
        const voor = bevrorenStand ?? view;
        const offerVoor = view.offer;
        const figurenVoor = figurenNu();
        // Eerst de rij vullen, dán verversen. `ververs()` speelt het beurtklankje zodra de
        // stand "jij bent aan de beurt" zegt, en de poort daarop is de `afspelen`-marker —
        // die pas gezet wordt door `zetInDeRij`. Andersom klonk jouw nieuwe beurt al
        // voordat het afspelen van zíjn beurt begonnen was.
        zetInDeRij({ zetten, gat, voor, vorigeFiguren: figurenVoor, vorigeOffer: offerVoor });
        ververs();

        // Hier stond een handgemaakte melding "Het handelsaanbod is aangenomen/afgewezen":
        // die was er precies omdat de zet zelf ontbrak. Nu komt het `ruil-antwoord` gewoon
        // in de rij en maakt het afspelen er zijn eigen mededeling van — hem hier laten
        // staan zou de speler hetzelfde bericht twee keer geven.

        // Verandert de stand terwijl jij midden in een handeling zit, dan wijst die
        // handeling naar een wereld die niet meer bestaat. Afbreken, met uitleg.
        if (HANDELING.has(modusVoor)) {
          modus = { kind: 'normaal' };
          sluitOverlay();
          weiger('Er is intussen iets gebeurd; je handeling is afgebroken.');
        }
        // Wachtte je nog op de ander om klaar te kunnen zijn, dan is dit het moment.
        if (probeerOpzetKlaar()) return;
        render();
      },
      onSessieWeg: () => {
        ik = undefined;
        tekenAccount();
        meld('Je sessie is verlopen. Log opnieuw in om verder te spelen.');
        render();
      },
    });
    neemPotjeOver(nieuw);
    huidigPotjeId = gameId;
    scherm = 'spel';
    ververs();
    TEGENSTANDER_NAAM = view.opponent.name;
    // De server weet welk figuur jij in dít potje speelt — juist in een nieuw tabblad,
    // waar de browser dat niet meer weet. Zonder dit hervatte je altijd als Louis.
    const bewaard = nieuw.figuur?.();
    if (bewaard !== undefined && bewaard !== '') {
      EIGEN_FIGUUR = bewaard;
      hud.zetSpelerFiguur(bewaard);
    }
    // Staat het potje nog in de opzet, dan is de vraag "wil je zelf verdelen?" al
    // beantwoord — bij het aanmaken van het potje of van de kamer. Hem hier nog eens
    // stellen zou erger zijn dan overbodig: op "ja" of "nee" maakt het scherm een níéuw
    // potje, en dan speel je verder in een partij waar je tegenstander niet in zit.
    modus =
      view.phase === 'opzet' ? { kind: 'verdeel-landschappen' } : { kind: 'normaal' };
    // Staat het nog in de opzet, dan begint dit potje nu pas — dan hoort de fanfare van
    // een nieuw spel erbij. Hervat je een potje dat al loopt, dan begin je niets en blijft
    // het stil.
    if (view.phase === 'opzet') playSound('effecten', FX.nieuwSpel);
    render();
  } catch (e) {
    meld(
      `Dat potje kon niet geopend worden (${e instanceof Error ? e.message : 'onbekend'}). ` +
        'Ververs de pagina om het opnieuw te proberen.',
    );
  }
}

/**
 * Het echte potje beginnen, nu je hebt gekozen of je zelf verdeelt.
 *
 * Ingelogd? Dan op de server, en dan blijft het bewaard. Niet ingelogd, of de server doet
 * het even niet? Dan hier, en dan is het weg zodra je het tabblad sluit.
 */
async function startPotjeMet(zelfVerdelen: boolean): Promise<void> {
  if (ik !== undefined && !lokaalGevraagd()) {
    const id = await maakOefenpotje({
      tegenstander: TEGENSTANDER_NAAM,
      figuur: EIGEN_FIGUUR,
      moeilijkheid: MOEILIJKHEID,
      zelfVerdelen,
      ...(vastLot() === undefined ? {} : { seed: vastLot() }),
    });
    if (id !== undefined) {
      await opentPotje(id);
      if (huidigPotjeId === id) return;
    }
    meld('Het potje kon niet op de server beginnen; je speelt nu in deze browser.');
  }
  beginPotje(nieuweSeed(), zelfVerdelen);
  render();
}

const el = (id: string): HTMLElement => {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`element ${id} ontbreekt`);
  return node;
};

// ---------------------------------------------------------------- zetten

/**
 * Een zet doen. Geeft terug of hij doorging.
 *
 * Die uitkomst doet ertoe voor de schermen die ergens middenin staan — het aanwijzen van
 * een bouwplek bijvoorbeeld. Weigert de kern de zet, dan hoor je in die modus te blijven
 * staan in plaats van eruit te vallen en opnieuw te moeten betalen.
 */
async function doAction(action: Action): Promise<boolean> {
  // De bevriezing gaat alleen over wát er getekend wordt, niet over wat je mag doen. Zetten
  // hier weigeren zolang een worp uitspeelt leek netjes — er valt tijdens het rollen toch
  // niets te doen — maar het slikte klikken die daarna wél moesten aankomen, en dan stond
  // een potje tussen twee mensen stil. De kern beslist wat mag; dit scherm niet.
  //
  // De stand van vóór de zet: die hebben we erna nog nodig — om te weten welke tegenkaart
  // er is ingezet, en om te zien wat er aan grondstoffen bij kwam.
  const voorZet = view;
  const vorigePending = view.pending;
  const vorigeOffer = view.offer;
  const vorigeFiguren = figurenNu();

  const uitkomst = await spel.zet(action);
  if (!uitkomst.ok) {
    weiger(`Dat kan niet: ${uitkomst.reden ?? 'dat mag nu niet'}`);
    return false;
  }
  // Speelde de server ná jouw zet nog voor de computer door, dan komen die zetten hier mee.
  // Zonder dit zie je in een online oefenpotje precies dezelfde leegte als tegen een mens:
  // `netpotje.computerAanZet()` geeft `false`, dus `runComputer` valt stil en niemand speelt
  // die beurt af.
  //
  // Vóór `ververs()`, om dezelfde reden als in `onNieuweStand`: het beurtklankje kijkt naar
  // de nieuwe stand en de poort ertegen is de `afspelen`-marker die hier gezet wordt.
  // Andersom hoorde je jouw nieuwe beurt al instarten terwijl de computer op het scherm
  // nog moest beginnen.
  //
  // `voorZet` mag hier: de enige zetten waarna de server doorspeelt zijn
  // `beurt-beeindigen`, een `ruil-antwoord` in zijn beurt en `opzet-klaar` — en voor géén
  // daarvan roept `naZet` `laatOpbrengstAankomen` aan. Er kan dus niets twee keer komen
  // aanvliegen. Komt er ooit een actie bij die én opbrengst geeft én de beurt overdraagt,
  // dan breekt die aanname.
  if (uitkomst.zetten !== undefined && uitkomst.zetten.length > 0) {
    zetInDeRij({
      zetten: uitkomst.zetten,
      gat: false,
      voor: voorZet,
      vorigeFiguren,
      vorigeOffer,
    });
  }
  ververs();
  naZet(action, uitkomst.log, vorigePending, vorigeOffer, vorigeFiguren, voorZet);
  render();
  planComputer();
  return true;
}

/**
 * De worp van een aanvalskaart in beeld brengen.
 *
 * Uit de eerste live test: "de aanvalsworp van de Zwarte ridder is onzichtbaar". De kern
 * rolde hem netjes en schreef hem in het verslag, maar het scherm sprong naar de uitkomst.
 *
 * De tekst komt uit het verslag zelf en wordt hier niet opnieuw bedacht. Wie er wint hangt
 * af van de worp én van een eventuele tegenkaart; die afweging staat in de regelkern en
 * hoort niet in tweevoud te bestaan.
 */
async function toonAanvalsworp(log: readonly string[]): Promise<void> {
  const worp = aanvalsworpUit(log);
  if (worp === undefined) return;

  // Zette de verdediger zijn Bisschop of Kruidenheks in, dan hoort dat vóór de worp te
  // staan — zo staat het ook in het handboek: eerst verweren, dan pas rollen.
  const verweer = log.find((regel) => / weert af met /.test(regel));
  if (verweer !== undefined) {
    toonMededeling(verweer, BANNER_MS);
    await wacht(RUST_MS + BANNER_MS / 2);
  }

  const uitslag = log.find((regel) => /^worp [1-6] — /.test(regel))?.replace(/^worp [1-6] — /, '');
  // Welke aanval het is staat in de regel die `playActionCard` als eerste schrijft. Zonder
  // die naam zie je een steen rollen zonder te weten waarvoor.
  const aanval = AANVALSKAARTEN.find((naam) =>
    log.some((regel) => regel.includes(` speelt ${naam}`)),
  );
  // Dezelfde klank als de worp aan het begin van je beurt: een steen die geluidloos rolt
  // leest niet als dobbelen.
  playSound('effecten', worpKlank(worpen++));
  await aanvalsWorp(board.animaties, worp, uitslag ?? '', aanval);

  // En dan de afloop als banner met de kaart zelf erin: de Kruidenheks of Bisschop als
  // er afgeweerd is — dat is het verhaal — en anders de aanvalskaart. De worp in de
  // banner herhaalt wat de steen liet zien, zodat wie even wegkeek het alsnog leest.
  const banierNaam = /weert af met de (.+)$/.exec(verweer ?? '')?.[1] ?? aanval;
  const banierKaart = BASIC_CARDS.find((c) => c.name === banierNaam);
  if (uitslag !== undefined && banierKaart !== undefined) {
    toonBanner('aanval', `Worp ${worp} — ${uitslag}`, banierKaart.id);
    await wacht(RUST_MS + BANNER_MS);
  }
}

/** De drie kaarten die met een dobbelsteen beslecht worden (handboek p. 11). */
const AANVALSKAARTEN = ['Zwarte ridder', 'Vuurduivel', 'Rooftocht'] as const;

/**
 * Wat er na elke zet in beeld moet gebeuren — voor jou en de computer gelijk.
 *
 * Juist bij de tegenstander telt dit: zonder de kaart groot in beeld zie je alleen een
 * regel in het verslag voorbijkomen en heb je geen idee wat er net gebeurde.
 */
function naZet(
  action: Action,
  log: readonly string[],
  vorigePending: GameView['pending'],
  vorigeOffer: GameView['offer'],
  vorigeFiguren: Figuren,
  voorZet: GameView,
  seat: Seat = JIJ,
): void {
  // De klank bij de zet zelf. Stond vroeger in `doAction`, en dat is precies de plek waar
  // de computer níét langskomt: hij bouwde en gooide geluidloos, terwijl jij bij dezelfde
  // zet wél iets hoorde. Hier komen beide spelers langs.
  klankBij(action);
  klankBijLog(log);
  // De kaart die groot in beeld komt, als die er is. De banner van een speelfiguur wacht
  // daarop: zolang de kaart er staat is er geen plek voor.
  let kaartKlaar: Promise<void> | undefined;

  // Bouwt de computer iets, dan hoort dat net zo goed te klinken en te flikkeren als bij
  // jou — anders staat er ineens een stad die je niet zag komen.
  if (seat === COMPUTER) {
    if (action.type === 'bouw-straat' || action.type === 'bouw-dorp' || action.type === 'bouw-stad') {
      bezet(1400);
      bouwFeedback(COMPUTER, action.column, 'midden');
      const wat = action.type.slice(5);
      toonMededeling(`${TEGENSTANDER_NAAM} bouwt een ${wat}.`, 1200);
    }
    if (action.type === 'bouw-uitbreiding') {
      bezet(1200);
      bouwFeedback(COMPUTER, action.column, action.side);
    }
  }

  // Bij "Goed jaar" en het riddertoernooi wijst een speler een landschap aan, en pas
  // daarna geeft de kern de opbrengst van de worp vrij. Allebei moet je zien aankomen —
  // hier stond een blokje dat alleen de gekozen grondstof liet vliegen en de opbrengst
  // van de worp helemaal niet.
  if (action.type === 'kies' && vorigePending?.kind === 'kies-opbrengst') {
    bevrorenStand = voorZet;
    void laatOpbrengstAankomen(voorZet);
  }

  // Ruilt de computer bij de bank, dan moet je dat zien gebeuren. Anders staan er ineens
  // drie grondstoffen minder en één meer, en lijkt het alsof hij ze uit het niets haalt.
  if (action.type === 'ruil-bank' && seat === COMPUTER) {
    const regel = log.find((r) => r.includes('bank')) ?? `${TEGENSTANDER_NAAM} ruilt bij de bank`;
    bezet(1800);
    toonMededeling(regel, 1600);
  }

  // En wat je van de bank terugkrijgt komt aanvliegen, net als de opbrengst van een worp.
  // Wat je gaf heb je zelf zien opstijgen bij het betalen; alleen wat erbij komt is nieuw.
  if (action.type === 'ruil-bank') void laatOpbrengstAankomen(voorZet);

  // Een aanbod dat jij doet mag niet ongemerkt voorbijgaan: eerst zie je dat het weg is,
  // dan komt het antwoord. Zonder die pauze flitst de hele ruil in één beeld voorbij.
  if (action.type === 'ruil-aanbod') {
    const wie = view.offer?.from === JIJ ? TEGENSTANDER_NAAM : 'Jij';
    bezet(1500);
    toonMededeling(`${wie === 'Jij' ? 'Je' : `${wie} `}denkt na over het aanbod…`, 1300);
  }

  // En het antwoord erop is in het origineel een mededeling in beeld, geen regeltje in
  // de balk (`catan-ref/shots/mij/handelsaanbod-afgewezen.png`).
  if (action.type === 'ruil-antwoord' && vorigeOffer !== undefined) {
    const aanbieder = vorigeOffer.from === JIJ ? 'Jij' : TEGENSTANDER_NAAM;
    if (!action.accepteren) {
      bezet(2000);
      toonMededeling(
        aanbieder === 'Jij'
          ? 'Het handelsaanbod is afgewezen!'
          : `Je wees het aanbod van ${TEGENSTANDER_NAAM} af.`,
      );
    } else {
      bezet(3200);
      toonMededeling('Het handelsaanbod is aangenomen!', 1400);
      window.setTimeout(() => void ruilAnimatie(vorigeOffer), 1400);
    }
  }

  if (action.type === 'gooi' && view.dice) {
    const gebeurtenis = view.dice.event;
    // Viel het vraagteken, dan is er een gebeurteniskaart getrokken. Die hoort eerst
    // groot in beeld te komen — pas daarna vertelt de banner wat er gebeurt. Zo zie je
    // wélke kaart het was; een regel in de balk zegt dat niet.
    const kaartId = view.lastEventCard;
    const worp = view.dice.production;
    // Het bord blijft staan waar het stond tot de hele beurt is uitgespeeld.
    bevrorenStand = voorZet;
    bezet(WORP_MS + RUST_MS);
    worpStilTot = performance.now() + WORP_MS;
    // Een gebeurtenis die niemand raakt hoeft geen banner: het origineel meldt alleen wat
    // er écht gebeurt. Bij gelijke handelsmacht of ridderpunten krijgt niemand iets, en
    // dan is een venster met "de grootste handelsmacht pakt een grondstof" onzin.
    const zonderGevolg = log.some((regel) => /niemand|er gebeurt niets/.test(regel));

    // De beurt speelt zich in vaste volgorde af: eerst rollen de stenen uit, dan komt de
    // gebeurtenis, en de opbrengst is altijd het laatst. Vroeger stonden deze stappen als
    // losse afspraken naast elkaar en verscheen de opbrengst al terwijl de steen rolde.
    void (async () => {
      try {
        await speelWorpUit(worp, gebeurtenis, kaartId, zonderGevolg, voorZet);
      } finally {
        // Wat er ook misgaat onderweg: het bord komt weer los. Een bevriezing die blijft
        // hangen is een potje dat stilstaat, en dat is erger dan een animatie die hapert.
        bevrorenStand = undefined;
        render();
      }
    })();
  }

  if (action.type === 'tegenkaart' && action.inzetten) {
    const vraag = vorigePending;
    if (vraag?.kind === 'kies-tegenkaart') {
      const kaart = BASIC_CARDS.find((c) => c.id === vraag.counterId);
      bezet(KAART_MS + RUST_MS);
      kaartKlaar = kaartInBeeld(board.animaties, vraag.counterId, kaart?.rules ?? '');
    }
  }

  if (action.type === 'speel-actiekaart') {
    // Het origineel zet de regeltekst van de kaart in het groene vak, niet een bijschrift
    // als "Jij speelt X" — de kaart vertelt zelf wat hij doet.
    const kaart = BASIC_CARDS.find((c) => c.id === action.cardId);
    bezet(KAART_MS + RUST_MS);
    kaartKlaar = kaartInBeeld(board.animaties, action.cardId, kaart?.rules ?? '');
  }

  // Ook een uitbouwkaart komt groot in beeld zodra hij staat — met zijn regeltekst in het
  // balkje, zoals `catan-ref/shots/mij/grote-kaart-als-je-gebouw-bouwt.png` laat zien.
  if (action.type === 'bouw-uitbreiding') {
    const kaart = BASIC_CARDS.find((c) => c.id === action.cardId);
    bezet(KAART_MS + RUST_MS);
    kaartKlaar = kaartInBeeld(board.animaties, action.cardId, kaart?.rules ?? '');
  }

  // Was dit een aanval, dan rolt de dobbelsteen — ná de kaart, want die vertelt eerst
  // wélke aanval het is. Vergeet-en-vergeef zoals de rest van `naZet`; het afspeelpad voor
  // andermans zetten wacht er wél op.
  if (aanvalsworpUit(log) !== undefined) {
    const worpKlaar = kaartKlaar ?? Promise.resolve();
    bezet(KAART_MS + WORP_MS + BANNER_MS);
    // De uitslagregel staat op dit moment al in het verslag, maar de steen moet nog
    // rollen. Zonder dit venster vertelt de smalle balk bovenin de afloop eerder dan het
    // spel hem laat zien — precies de klacht uit de tweede livetest.
    worpStilTot = performance.now() + KAART_MS + WORP_MS + BANNER_MS;
    void worpKlaar.then(() => toonAanvalsworp(log));
  }

  // En tot slot: is er een speelfiguur van eigenaar gewisseld, dan hoort daar een banner
  // bij. Hij wacht netjes tot de grote kaart weg is — die staat er anders nog overheen.
  const meldingen = figuurMeldingen(vorigeFiguren, figurenNu(), spelerNaam, JIJ);
  if (meldingen.length > 0) {
    bezet(KAART_MS + meldingen.length * (RUST_MS + BANNER_MS));
    void (kaartKlaar ?? Promise.resolve()).then(() => toonFiguurBanners(meldingen));
  }
}

/**
 * De beurt van de ander, afgespeeld.
 *
 * Dit is de reparatie van de zwaarste klacht uit de eerste live test: de wachtende speler
 * zag niets. Geen worp, geen kaart, geen bouwflits — de stand sprong gewoon bij.
 *
 * Waarom hier een tweede route naast `naZet` staat en die niet hergebruikt wordt: `naZet`
 * is synchroon en vergeet-en-vergeef. Hij *start* animaties (`void (async () => …)`) en
 * regelt de volgorde met tijdreserveringen via `bezet(ms)`. Voor één zet werkt dat; voor
 * een reeks moet je juist wáchten, anders rolt de dobbelsteen door de bouwflits heen.
 * Bovendien leest `naZet` voor drie takken de eíndstand — hem N keer aanroepen voor N
 * zetten geeft N identieke dobbelanimaties. De bladfuncties eronder zijn wél dezelfde.
 */

/** Eén partij zetten die nog afgespeeld moet worden, mét de stand van ervóór. */
interface Afspeelpartij {
  readonly zetten: readonly Zichtbarezet[];
  readonly gat: boolean;
  readonly voor: GameView;
  readonly vorigeFiguren: Figuren;
  readonly vorigeOffer: GameView['offer'];
}

/**
 * Een wachtrij, geen vlag.
 *
 * Een slot dat binnenkomende berichten wéggooit terwijl er een animatie loopt verliest
 * zetten — en dan mist de speler precies wat hij had moeten zien. Alles komt in de rij; de
 * rij loopt leeg in de volgorde waarin hij vol liep.
 */
let afspeelrij: Afspeelpartij[] = [];
/** Hoeveel zetten van de ander er zijn afgespeeld. Alleen als haakje voor de toetsen. */
let afgespeeld = 0;

function zetInDeRij(partij: Afspeelpartij): void {
  if (partij.zetten.length === 0) return;
  afspeelrij.push(partij);
  afspelen ??= leegDeRij().finally(() => {
    afspelen = undefined;
    // Het beurtklankje hoort híér, meteen na de slotpauze — niet bij de volgende poll.
    // Zonder deze aanroep bleef het stil tot de server weer iets stuurde, en tegen die
    // tijd rolde jouw dobbelsteen al: de aankondiging kwam dwars door je eigen worp heen.
    // Is het helemaal geen nieuwe beurt (de ander is nog bezig), dan beslist
    // `volgendeBeurtklank` dat zelf en blijft het stil.
    beurtklank();
    // Zonder deze tekenbeurt blijft `dobbelVanzelf` uit: die slaat over zolang er
    // afgespeeld wordt, en wordt alleen vanuit `render()` opnieuw geprobeerd.
    render();
  });
}

async function leegDeRij(): Promise<void> {
  while (afspeelrij.length > 0) {
    // De héle rij in één keer, en de vóór-stand van het eerste stuk. Dat is geen zuinigheid
    // maar ijking: `laatOpbrengstAankomen(voor)` toont het verschil tussen die stand en nu,
    // en dat is precies alles wat er sindsdien gebeurde. Per binnenkomst apart afspelen zou
    // de tweede ronde tegen een verouderde `voor` afzetten.
    const partij = afspeelrij.splice(0);
    const eerste = partij[0];
    if (eerste === undefined) return;
    await speelAndermansZetten(
      partij.flatMap((p) => [...p.zetten]),
      partij.some((p) => p.gat),
      eerste.voor,
      eerste.vorigeFiguren,
      eerste.vorigeOffer,
    );
  }
}

async function speelAndermansZetten(
  zetten: readonly Zichtbarezet[],
  gat: boolean,
  voorZet: GameView,
  vorigeFiguren: Figuren,
  vorigeOffer: GameView['offer'],
): Promise<void> {
  // Met beweging uit duurt alles een fractie van de gewone tijd, dus dan hoeft er niets
  // gecondenseerd te worden. `KOSTEN` in de planner gaat uit van de volle animaties; hier
  // corrigeren we daarvoor in plaats van die tabel te laten meebewegen — de planner blijft
  // zo puur rekenwerk zonder weet van de voorkeuren van de browser.
  const { stappen: plan, gedekt } = afspeelplan(zetten, {
    gat,
    ...(stil ? { budget: Number.MAX_SAFE_INTEGER } : {}),
  });

  // Klank alleen voor zetten die géén stap in het plan kregen — het enige spoor van wat er
  // visueel wegviel. Gedekte zetten klinken op het moment dat hun stap in beeld komt; alles
  // vooraf spelen betekende bouwgeluiden terwijl de steen nog rolde. De worpklank blijft
  // hier áltijd weg: zonder plan is er geen rollende steen, en een steen die je alleen
  // hoort is erger dan er geen horen.
  for (const zet of zetten) {
    if (zet.actie.type !== 'gooi' && !gedekt.has(zet.seq)) klankBij(zet.actie as Action);
    klankBijLog(zet.log);
  }
  if (plan.length === 0) {
    afgespeeld += zetten.length;
    return;
  }

  const speelStap = async (stap: (typeof plan)[number]): Promise<void> => {
    if (stap.soort === 'bouw') {
      bezet(BOUW_MS);
      playSound('effecten', bouwklank(stap.cardId));
      bouwFeedback(stap.seat, stap.column, stap.side);
      toonMededeling(`${spelerNaam(stap.seat)} bouwt.`, BOUW_MS);
      await wacht(BOUW_MS);
    } else if (stap.soort === 'kaart') {
      const kaart = cardById.get(stap.cardId);
      bezet(KAART_MS + RUST_MS);
      await kaartInBeeld(board.animaties, stap.cardId, kaart?.rules ?? '');
    } else if (stap.soort === 'worp' && (stap.production !== undefined || view.dice)) {
      // De stap draagt zijn eigen worp, gelezen uit het verslag van de gooi-zet. De
      // levende weergave is hem namelijk al kwijt zodra de beurt in dezelfde partij
      // ook eindigde — en dan sloeg het scherm de steen stilzwijgend over terwijl de
      // bouwflitsen al draaiden ("hij bouwde direct tijdens het rollen", achtste
      // potje). `view.dice` blijft als vangnet voor een partij zonder leesbaar
      // verslag.
      worpStilTot = performance.now() + WORP_MS;
      const zonderGevolg = zetten.some((z) =>
        z.log.some((r) => /niemand|er gebeurt niets/.test(r)),
      );
      playSound('effecten', worpKlank(worpen++));
      await speelWorpUit(
        stap.production ?? view.dice?.production ?? 1,
        stap.event ?? view.dice?.event ?? 'goed-jaar',
        view.lastEventCard,
        zonderGevolg,
        voorZet,
      );
    } else if (stap.soort === 'ruil') {
      playSound('effecten', stap.aangenomen ? FX.gelukt : FX.ruilMislukt);
      toonMededeling(
        stap.aangenomen ? 'Het handelsaanbod is aangenomen!' : 'Het handelsaanbod is afgewezen!',
      );
      if (stap.aangenomen && vorigeOffer !== undefined) await ruilAnimatie(vorigeOffer);
    } else if (stap.soort === 'aanval') {
      worpStilTot = performance.now() + WORP_MS + BANNER_MS;
      await toonAanvalsworp(stap.regels);
    } else if (stap.soort === 'gestolen') {
      // Het slachtoffer ziet wélke kaart weg is. Zonder dit zag hij alleen zijn hand
      // krimpen — precies de klacht uit de eerste live test.
      const kaart = BASIC_CARDS.find((c) => c.name === stap.naam);
      if (kaart !== undefined) {
        bezet(KAART_MS + RUST_MS);
        await kaartInBeeld(board.animaties, kaart.id, kaart.rules ?? '');
      }
    } else if (stap.soort === 'kaartwissel') {
      playSound('effecten', FX.kaartNeergelegd);
      toonMededeling(
        stap.wat === 'ruil'
          ? `${spelerNaam(stap.seat)} ruilt een handkaart onder een stapel.`
          : `${spelerNaam(stap.seat)} vult zijn hand aan.`,
        stil ? 200 : 1400,
      );
      await wacht(stil ? 100 : 1200);
    } else if (stap.soort === 'opbrengst') {
      await laatOpbrengstAankomen(voorZet);
    }
    // Een `worp` zonder `view.dice` valt hier stilzwijgend weg. Expliciet zo geschreven:
    // met een kale `else` zou hij de opbrengst-tak binnenlopen en het verschil twee keer
    // laten aanvliegen.
  };

  // Bevriezen op de oude stand, zodat het bord niet al bijgesprongen is terwijl de animatie
  // nog moet beginnen. De `finally` is geen netheid: een bevriezing die blijft hangen is
  // een stilstaand potje.
  noteer('afspeel-start', plan.map((s) => s.soort).join(','));
  bevrorenStand = voorZet;
  try {
    for (const stap of plan) {
      noteer('stap-start', stap.soort);
      // Geen enkele stap mag de rij gijzelen. De animatie-beloften (dobbelsteen, kaart,
      // vliegende grondstoffen) hangen aan de tekenklok, en op een zwaarbelaste machine
      // kan die stilvallen — dan bleef het bord eeuwig bevroren met een banner in beeld
      // en kwam jouw worp nooit (gevangen door de tijdlijn-momentopname op een tweede
      // platform). De wedloop kapt hooguit de vertoning af; de stand komt uit de kern
      // en de `finally` hieronder maakt het bord hoe dan ook weer los. Grijpt het
      // vangnet in, dan staat dat in de opname — bij een val is dan te zíén of dit
      // dezelfde hapering was of iets nieuws, in plaats van erover te gissen.
      // De grenzen: ruim tweemaal de traagste stap in die modus, en krap genoeg dat een
      // heel venster op een stroperige machine binnen het geduld van de toetsen blijft.
      const opTijd = await Promise.race([
        speelStap(stap).then(() => true),
        wacht(stil ? 4_000 : 20_000).then(() => false),
      ]);
      if (!opTijd) noteer('stap-afgekapt', stap.soort);
      noteer('stap-einde', stap.soort);

      // Adem tussen de stappen: zonder deze rust rijgen de animaties aan elkaar en is de
      // beurt van de ander een flits in plaats van iets om naar te kijken.
      await wacht(stil ? 60 : RUST_MS);
    }

    const meldingen = figuurMeldingen(vorigeFiguren, figurenNu(), spelerNaam, JIJ);
    if (meldingen.length > 0) await toonFiguurBanners(meldingen);

    // En een slotpauze vóór het stokje overgaat: de laatste bouwflits hoort te landen
    // voordat jouw beurtklankje eroverheen komt — "hij was nog iets aan het bouwen en
    // toen hoorde ik al de nieuwe beurt instarten."
    await wacht(stil ? 100 : 1000);
  } finally {
    noteer('afspeel-einde');
    bevrorenStand = undefined;
    afgespeeld += zetten.length;
    render();
  }
}

/**
 * De grondstoffen van een geslaagde ruil steken één voor één over.
 *
 * "Klik één voor één op het landschap of de landschappen waarvan de grondstoffen
 * afgegeven moeten worden. Klik daarna op het landschap of de landschappen waarvoor je
 * grondstoffen krijgt" (handboek p. 14). Wij klikken niet — de kern heeft de ruil al
 * gedaan — maar je moet wél zien wát er van hand wisselde, anders lijkt het alsof je
 * tegenstander er zomaar iets bij tovert.
 */
async function ruilAnimatie(offer: NonNullable<GameView['offer']>): Promise<void> {
  const ander = offer.from === JIJ ? COMPUTER : JIJ;

  /** Waar een grondstof van deze soort bij deze speler ligt. */
  const plek = (seat: Seat, soort: Resource): { x: number; y: number } => {
    const tegel = vorstendom(seat).landscapes.find(
      (t) => LANDSCAPE_RESOURCE[t.landscape] === soort,
    );
    return tegel === undefined
      ? { x: 512, y: seat === JIJ ? 560 : 200 }
      : board.schermPositie(seat, tegel.column, tegel.side);
  };

  const stappen: { soort: Resource; van: Seat; naar: Seat }[] = [];
  for (const [soort, aantal] of Object.entries(offer.give)) {
    for (let i = 0; i < (aantal ?? 0); i++) {
      stappen.push({ soort: soort as Resource, van: offer.from, naar: ander });
    }
  }
  for (const [soort, aantal] of Object.entries(offer.want)) {
    for (let i = 0; i < (aantal ?? 0); i++) {
      stappen.push({ soort: soort as Resource, van: ander, naar: offer.from });
    }
  }

  for (const stap of stappen) {
    await grondstofVliegtNaar(
      board.animaties,
      stap.soort,
      plek(stap.van, stap.soort),
      plek(stap.naar, stap.soort),
    );
  }
  render();
}

/**
 * De worp start vanzelf zodra een beurt begint.
 *
 * Eén uitzondering: de Alchimist speel je juist *voor* het gooien ("Speel deze kaart
 * voordat je met de dobbelsteen gooit"). Heb je die op de hand, dan stellen we de vraag —
 * anders zou de kaart onspeelbaar zijn.
 */
let dobbeltAl = false;
/** "Nee" gezegd tegen de Alchimist? Dan die beurt niet opnieuw vragen. */
let alchimistOvergeslagen = false;
function dobbelVanzelf(): void {
  if (dobbeltAl || view.winner !== undefined) return;
  // Speelt de beurt van de ander nog uit, dan wachten we. Zonder deze rem rolt jouw
  // dobbelsteen dwars door zijn banner heen zodra de beurt overgaat — `render()` roept dit
  // aan, en dat gebeurt tijdens het afspelen ook. `leegDeRij` sluit af met `render()`, dus
  // hierna wordt het vanzelf opnieuw geprobeerd.
  if (afspelen !== undefined) return;
  if (view.phase !== 'dobbelen' || view.pending !== undefined) {
    // Buiten de dobbelfase mag de vraag de volgende beurt gewoon opnieuw komen.
    alchimistOvergeslagen = false;
    return;
  }
  if (view.turn !== JIJ) {
    alchimistOvergeslagen = false;
    return;
  }

  const alchimist = view.legalActions.some(
    (a) => a.type === 'speel-actiekaart' && BASIC_CARDS.find((c) => c.id === a.cardId)?.name === 'Alchimist',
  );
  if (alchimist && !alchimistOvergeslagen) {
    vraagAlchimist();
    return;
  }

  dobbeltAl = true;
  noteer('dobbelvanzelf');
  window.setTimeout(() => {
    dobbeltAl = false;
    if (view.phase === 'dobbelen' && view.turn === JIJ && view.pending === undefined) {
      void doAction({ type: 'gooi' });
    }
    // Via `wachttijd` en niet via een vast getal: aan het begin van een beurt loopt het
    // beurtklankje nog, en de stenen horen daarachteraan te komen.
  }, wachttijd(600));
}

/**
 * De vraag van de Alchimist, aan het begin van je beurt.
 *
 * De kern bood de kaart al aan — `targetsFor` zet zes zetten klaar, één per worp — maar
 * geen enkel scherm liet je het getal kiezen. Wie de kaart aanklikte speelde via
 * `find()` stilzwijgend de eerste variant: áltijd worp 1. Vandaar nul Alchimisten in
 * 323 zetten: de kaart hield alleen je automatische worp tegen.
 */
function vraagAlchimist(): void {
  // `render()` komt hier elke tekenbeurt langs; een open venster niet verdringen.
  if (overlay !== undefined) return;
  toonDialoog('Wil je de Alchimist inzetten? Jij bepaalt dan de worp van de opbrengst.', [
    {
      soort: 'ja',
      onClick: () => {
        sluitOverlay();
        toonAlchimistWorp();
      },
    },
    {
      soort: 'nee',
      label: 'Gooien',
      onClick: () => {
        alchimistOvergeslagen = true;
        sluitOverlay();
        render();
      },
    },
  ]);
}

/** De zes vlakken; de klik speelt de kaart met precies dat worpgetal. */
function toonAlchimistWorp(): void {
  const keuzes = view.legalActions.filter(
    (a) => a.type === 'speel-actiekaart' && a.target?.kind === 'worp',
  );
  if (keuzes.length === 0) return;
  noteer('vraag', 'alchimist-worp');
  sluitOverlay();
  const laag = worpKeuze({
    vraag: 'De Alchimist — kies de worp van de opbrengst.',
    waarden: keuzes
      .map((a) => (a.type === 'speel-actiekaart' && a.target?.kind === 'worp' ? a.target.value : 0))
      .filter((w) => w > 0),
    onKies: (waarde) => {
      sluitOverlay();
      const actie = keuzes.find(
        (a) => a.type === 'speel-actiekaart' && a.target?.kind === 'worp' && a.target.value === waarde,
      );
      if (actie !== undefined) void doAction(actie);
    },
  });
  overlay = { soort: 'dialoog', laag };
  hud.vensters.addChild(laag);
  playSound('interface', UI.venster);
}

/** Vloten hebben hun eigen klank; de rest is het gewone bouwgeluid. */
function bouwklank(cardId?: number): string {
  const naam = cardId === undefined ? '' : (cardById.get(cardId)?.name ?? '');
  return naam.endsWith('vloot') ? FX.vlootGebouwd : FX.gebouwGebouwd;
}

function klankBij(action: Action): void {
  if (action.type === 'gooi') playSound('effecten', worpKlank(worpen++));
  else if (action.type === 'bouw-uitbreiding') {
    playSound('effecten', bouwklank(action.cardId));
  } else if (action.type.startsWith('bouw-')) playSound('effecten', FX.gebouwGebouwd);
  else if (action.type === 'pak-kaart' || action.type === 'ruil-kaart') {
    playSound('effecten', FX.kaartNeergelegd);
  } else if (action.type === 'ruil-antwoord') {
    playSound('effecten', action.accepteren ? FX.gelukt : FX.ruilMislukt);
  } else if (action.type.startsWith('ruil-')) playSound('effecten', FX.kaartNeergelegd);
  // Een actiekaart krijgt hier géén klank: hij komt groot in beeld en brengt dan zijn
  // eigen muziekstukje mee (`cards_NNN`). Er stond een vast gebeurtenisstuk overheen, en
  // dat maakte elke kaart hetzelfde.
}

/**
 * Welk muziekstuk bij welke gebeurtenis hoort.
 *
 * `snd/event.BCF` bevat vier stukken en de gebeurtenissendobbelsteen heeft vier vlakken
 * naast het vraagteken. De platen van dezelfde schijf (`EVENT_000`–`EVENT_004`) staan
 * aantoonbaar in de volgorde feestmaal, handelaar, overval, ridders, brand — dat zie je
 * aan de afbeeldingen. De klanken volgen diezelfde nummering. Het vraagteken heeft er
 * geen: dat trekt een kaart, en die brengt zijn eigen muziek mee.
 *
 * Hiervoor werd `event_000` gespeeld zodra het woord "roofoverval", "pest" of
 * "burgeroorlog" in het verslag stond — op tekst dus, en nog het verkeerde stuk ook: bij
 * een overval klonk de feestmuziek.
 */
const GEBEURTENIS_KLANK: Readonly<Record<string, string>> = {
  'goed-jaar': 'event_000',
  handelsvoordeel: 'event_001',
  roofoverval: 'event_002',
  riddertoernooi: 'event_003',
};

/** Het toernooistuk, zolang de winnaar zijn grondstof nog moet uitzoeken. */
let toernooiLoopt = false;
/** Eén geplande hertoets zodra het bezet-venster vrijkomt; nooit meer dan één tegelijk. */
let toernooiHertoets: number | undefined;

function stopToernooi(): void {
  if (!toernooiLoopt) return;
  toernooiLoopt = false;
  stopVerhaal();
}

/**
 * `FX_008` — "toernooi", zeventien seconden lang.
 *
 * Het riddertoernooi heeft twee beats: de aankondiging, en daarna de winnaar die zijn
 * grondstof uitzoekt. De aankondiging heeft zijn eigen stuk (`event_003`, gemeten op de
 * volgorde van de gebeurtenisplaten), dus dit lange stuk hoort bij de tweede: het loopt
 * zolang de keuze openstaat en stopt zodra er gekozen is.
 *
 * Het wacht tot de banner weg is. Beide stukken delen de verhaalbaan, dus zonder dat
 * wachten zou het de aankondiging meteen weer wegvagen.
 */
function toernooiKlank(): void {
  const kiest =
    view.pending?.kind === 'kies-opbrengst' && view.pending.reason === 'riddertoernooi';
  // Ook wachten zolang de wórp nog speelt (en tijdens het afspelen van andermans beurt):
  // de keuzevraag staat al in de stand terwijl de steen nog rolt, en dan begon dit
  // lange stuk vóór de aankondiging uit — twee toernooimuziekjes over elkaar. De banner
  // sluit met een render, dus zodra alles gespeeld is start dit stuk vanzelf alsnog.
  //
  // `bezetTot` erbij, want `worpStilTot` dekt alleen de rollende steen: tussen het einde
  // van de animatie en het opengaan van de banner zit een tekenbeurt, en precies in die
  // kier startte dit stuk alsnog te vroeg. Het bezet-venster omspant de hele presentatie
  // — worp, kaart én banner — dus daarbinnen zwijgt het toernooi.
  const nogBezig =
    performance.now() < worpStilTot ||
    performance.now() < bezetTot ||
    afspelen !== undefined;
  if (!kiest || overlay?.soort === 'banner' || nogBezig) {
    stopToernooi();
    // Verloopt het venster nét na de laatste tekenbeurt, dan zou dit stuk voorgoed
    // zwijgen: niemand tekent dan nog. Eén hertoets op de wekker, precies op het
    // moment dat het venster vrijkomt.
    if (kiest && nogBezig && toernooiHertoets === undefined) {
      const rest = Math.max(worpStilTot, bezetTot) - performance.now() + 150;
      toernooiHertoets = window.setTimeout(() => {
        toernooiHertoets = undefined;
        render();
      }, Math.max(rest, 150));
    }
    return;
  }
  if (toernooiLoopt) return;
  toernooiLoopt = true;
  speelVerhaal('effecten', FX.toernooi);
}

/**
 * Geluid bij wat er in het verslag verschijnt.
 *
 * Alléén geluid. Hier kwam ooit ook een dialoog uit ("Het handelsaanbod is afgewezen!",
 * met een OK-knop) — die was dubbelop: de ruil-antwoord-tak in `naZet` en de ruil-stap
 * van het afspelen melden de uitkomst allebei al op parkement, zoals het origineel
 * (`catan-ref/shots/mij/handelsaanbod-afgewezen.png`). En sinds de rij vóór het
 * verversen gevuld wordt, brak die dialoog bovendien het afspelen open — de
 * tijdlijn-proef ving hem als "vraag binnen het afspelen".
 */
function klankBijLog(regels: readonly string[]): void {
  for (const regel of regels) {
    // De jaarwisseling: de gebeurtenisstapel gaat opnieuw door elkaar. Het enige moment
    // in het spel waarop er echt geschud wordt, en dus de plek voor `FX_003`.
    if (isGeschud(regel)) playSound('effecten', FX.kaartenGeschud);
  }
}

/**
 * De enige lopende afspraak voor de computer.
 *
 * Elke zet plande er vroeger een nieuwe bij zonder de vorige af te zeggen. Zo stonden er
 * meerdere klaar, en een die al liep vóórdat jij antwoordde vuurde gewoon af — met als
 * gevolg dat een mededeling meteen weer overschreven werd en de beurt van de computer
 * langs je heen vloog. Eén afspraak tegelijk, en die kijkt bij het afgaan opnieuw of het
 * scherm al vrij is.
 */
let computerTimer: number | undefined;
/** Hoe vaak de computer op rij niets wist. */
let vastPogingen = 0;
function planComputer(basis = 700): void {
  if (computerTimer !== undefined) window.clearTimeout(computerTimer);
  computerTimer = window.setTimeout(runComputer, wachttijd(basis));
}

function runComputer(): void {
  computerTimer = undefined;
  if (view.winner !== undefined) return;
  // Staat er nog iets in beeld dat gelezen moet worden, dan wacht hij netjes af.
  if (performance.now() < bezetTot) {
    planComputer();
    return;
  }
  if (!spel.computerAanZet()) return;

  const voorZet = view;
  const vorigePending = view.pending;
  const vorigeOffer = view.offer;
  const vorigeFiguren = figurenNu();
  const choice = spel.computerZet();
  if (choice === undefined) {
    // Weet de computer even niets, dan mag dat het potje niet doen vastlopen: er staat
    // maar één afspraak open, dus wie hier zwijgend terugkeert bevriest het scherm.
    // Een paar keer opnieuw proberen, en anders zeggen dát hij vastzit.
    if (vastPogingen < 5) {
      vastPogingen += 1;
      planComputer(900);
      return;
    }
    meld(`${TEGENSTANDER_NAAM} kan niets meer doen. Sluit je beurt af of begin een nieuw potje.`);
    return;
  }
  vastPogingen = 0;
  ververs();
  naZet(choice.actie, choice.log, vorigePending, vorigeOffer, vorigeFiguren, voorZet, COMPUTER);
  render();
  // Een kaart die groot in beeld staat, moet je kunnen lezen voor de volgende zet komt.
  planComputer();
}

/** "2× hout en 1× wol", voor de bedieningslaag. */
function beschrijfBundel(bundel: Readonly<Partial<Record<Resource, number>>>): string {
  const delen = Object.entries(bundel)
    .filter(([, aantal]) => (aantal ?? 0) > 0)
    .map(([soort, aantal]) => `${aantal}× ${soort}`);
  return delen.length === 0 ? 'niets' : delen.join(' en ');
}

function meld(tekst: string): void {
  spel.meld(tekst);
  render();
}

/**
 * Een melding die "dat kan niet" zegt.
 *
 * Hetzelfde als `meld`, met het geluid erbij dat de speler `FX_001` gaf: "iets lukt
 * niet". Bewust niet aan `meld` zelf gehangen — de helft van de meldingen wijst je juist
 * de weg ("klik links een stapel aan"), en een afkeurend geluid onder een aanwijzing
 * leest als een standje.
 */
function weiger(tekst: string): void {
  playSound('effecten', FX.mislukt);
  meld(tekst);
}

// ---------------------------------------------------------------- weergave

/** Wat er bij elke gebeurtenis gebeurt, in de bewoordingen van het handboek. */
function uitlegVan(gebeurtenis: string): string {
  const uitleg: Record<string, string> = {
    'goed-jaar': 'Goed jaar — beide spelers kiezen een grondstof.',
    handelsvoordeel: 'Handelsvoordeel — de grootste handelsmacht pakt een grondstof.',
    roofoverval: 'Roofoverval — wie meer dan zeven grondstoffen heeft, verliest erts en wol.',
    riddertoernooi: 'Riddertoernooi — de meeste toernooipunten krijgt een grondstof.',
    gebeurteniskaart: 'Er wordt een gebeurteniskaart getrokken.',
  };
  return uitleg[gebeurtenis] ?? gebeurtenis;
}

/** Volledig scherm aan of uit. */
async function volledigScherm(): Promise<void> {
  if (document.fullscreenElement === null) {
    await document.documentElement.requestFullscreen().catch(() => {
      meld('Volledig scherm kan hier niet; installeer de app voor hetzelfde effect.');
    });
  } else {
    await document.exitFullscreen().catch(() => undefined);
  }
}

function handHtml(view: GameView): string {
  if (view.you.hand.length === 0) {
    return '<p class="grijs">Geen handkaarten — vul aan het eind van je beurt aan.</p>';
  }
  // "Speelbaar" is meer dan een actiekaart: het merendeel van het deck bestaat uit
  // uitbouwkaarten, en die bouw je in je vorstendom. Zonder ze hier mee te tellen bleven
  // ze in de hand hangen zonder dat het scherm iets aanbood.
  const zetten = view.legalActions;
  const speelbaar = new Set(
    zetten
      .filter((a) => a.type === 'speel-actiekaart' || a.type === 'bouw-uitbreiding')
      .map((a) =>
        a.type === 'speel-actiekaart' || a.type === 'bouw-uitbreiding' ? a.cardId : 0,
      ),
  );
  return view.you.hand
    .map((cardId) => {
      const naam = cardById.get(cardId)?.name ?? `kaart ${cardId}`;
      const plaat = hasAsset('kaarten', String(cardId))
        ? `<img src="${asset('kaarten', String(cardId)).src}" alt="${naam}" loading="lazy" />`
        : '';
      return `<button class="handkaart${speelbaar.has(cardId) ? ' speelbaar' : ''}"
                data-card="${cardId}" title="${naam}">${plaat}<span>${naam}</span></button>`;
    })
    .join('');
}

function highlightsVoor(): Highlights {
  const acties = view.legalActions;
  const mijn = view.you.principality;
  // Geen vraag-markeringen zolang de beurt van de ander nog uitspeelt: bij een "goed
  // jaar" moet jíj kiezen terwijl zijn worp hier nog rolt, en oplichtende landschappen
  // midden in zijn beurt lezen als "ik ben alweer aan de beurt". Na `leegDeRij` komt
  // alles vanzelf, met de aanwijzing in de balk erbij.
  const pending = afspelen === undefined ? view.pending : undefined;

  // Tijdens het betalen lichten alleen de landschappen op die nog van pas komen.
  if (modus.kind === 'betalen') {
    return {
      roadSpots: [],
      settlementSpots: [],
      upgradable: [],
      betaald: modus.betaald,
      pickTiles: {
        seat: JIJ,
        tiles: bruikbareLandschappen(view.you, modus.kosten, modus.betaald),
      },
    };
  }
  if (modus.kind === 'ruil-kies-doel') {
    const doelen = mijn.landscapes.filter((t) => t.resources < 3);
    return {
      roadSpots: [],
      settlementSpots: [],
      upgradable: [],
      pickTiles: { seat: JIJ, tiles: doelen.map((t) => ({ column: t.column, side: t.side })) },
    };
  }

  const alles = {
    roadSpots: acties.some((a) => a.type === 'bouw-straat') ? roadSpots(mijn) : [],
    settlementSpots: acties.some((a) => a.type === 'bouw-dorp') ? settlementSpots(mijn) : [],
    upgradable: acties
      .filter((a) => a.type === 'bouw-stad')
      .map((a) => (a.type === 'bouw-stad' ? a.column : -1)),
  };
  // Tijdens het verdelen zie je de nog lege plekken; daar klik je je landschap heen.
  if (modus.kind === 'verdeel-landschappen') {
    const bezet = new Set(mijn.landscapes.map((t) => `${t.column}:${t.side}`));
    return {
      roadSpots: [],
      settlementSpots: [],
      upgradable: [],
      emptyLandscapes: STARTPLEKKEN.filter((p) => !bezet.has(`${p.column}:${p.side}`)),
    };
  }

  // Koos je een uitbouwkaart, dan lichten alleen de plekken op waar hij bij past.
  if (modus.kind === 'kies-uitbouwplek') {
    const kaartId = modus.cardId;
    return {
      roadSpots: [],
      settlementSpots: [],
      upgradable: [],
      // Je hebt al betaald, dus die grondstoffen liggen er voor jouw gevoel niet meer.
      // Zonder dit sprongen ze bij de laatste klik allemaal terug op tafel — en dan lijkt
      // het alsof juist die laatste betaling niet is aangekomen.
      betaald: modus.payment,
      expansionSpots: acties.flatMap((a) =>
        a.type === 'bouw-uitbreiding' && a.cardId === kaartId
          ? [{ column: a.column, side: a.side }]
          : [],
      ),
    };
  }

  // Koos je een bouwstapel, dan lichten alleen de plekken van díé soort op — anders wijs
  // je zo een straatplek aan terwijl je een dorp wilde zetten.
  const basis =
    modus.kind === 'kies-bouwplek'
      ? {
          roadSpots: modus.bouw === 'straat' ? alles.roadSpots : [],
          settlementSpots: modus.bouw === 'dorp' ? alles.settlementSpots : [],
          upgradable: modus.bouw === 'stad' ? alles.upgradable : [],
          betaald: modus.payment,
        }
      : alles;
  if (pending?.seat !== JIJ) return basis;

  /** De landschappen van een speler, eventueel alleen die met iets erop. */
  const landschappen = (seat: Seat, alleenGevuld: boolean): { column: number; side: string }[] =>
    vorstendom(seat)
      .landscapes.filter((t) => !alleenGevuld || t.resources > 0)
      .map((t) => ({ column: t.column, side: t.side }));

  // Wélke markering bij welke vraag hoort staat in `markering.ts` — los van pixi en van de
  // schermtoestand, zodat een toets over álle vragen kan lopen. Daar staat ook waarom dat
  // bestandje bestaat: hier zat het vangnet dat twee gebeurtenissen liet vastlopen.
  const markering = markeringVoor(pending);
  switch (markering.soort) {
    case 'geen':
      return basis;
    case 'eigen-landschappen':
      return { ...basis, pickTiles: { seat: JIJ, tiles: landschappen(JIJ, false) } };
    case 'landschappen-van':
      return {
        ...basis,
        pickTiles: { seat: markering.seat, tiles: landschappen(markering.seat, true) },
      };
    case 'lege-plekken':
      // Het nieuwe dorp: de twee vrije plekken in de buitenkolom, boven en onder. Dezelfde
      // zandkleurige vakken als bij het verdelen van de startlandschappen.
      return {
        ...basis,
        emptyLandscapes: (['boven', 'onder'] as const)
          .filter(
            (side) =>
              !view.you.principality.landscapes.some(
                (t) => t.column === markering.column && t.side === side,
              ),
          )
          .map((side) => ({ column: markering.column, side })),
      };
    case 'gebouwd-van': {
      // Alleen de géldige doelen: de Burgeroorlog wijst ridders en vloten aan, een
      // mislukte Vuurduivel gebouwen. Hier lichtte élke gebouwde kaart op; klikken op de
      // verkeerde gaf een nette weigering, maar oplichten is een belofte — dan moet de
      // klik ook kunnen. `terugnameKandidaten` is dezelfde lijst die de kern hanteert.
      const soort = pending.kind === 'kies-terugname' ? pending.soort : undefined;
      const kandidaten =
        pending.kind === 'kies-terugname'
          ? terugnameKandidaten({ principality: vorstendom(markering.seat) }, soort)
          : vorstendom(markering.seat).buildings.flatMap((b) =>
              (['boven', 'onder'] as const)
                .filter((zijde) => b.expansions[zijde].length > 0)
                .map((zijde) => ({ column: b.column, side: zijde })),
            );
      return {
        ...basis,
        pickBuilt: {
          seat: markering.seat,
          plekken: kandidaten.map((k) => ({ column: k.column, side: k.side })),
        },
      };
    }
  }
}


/**
 * Wat de smalle balk bovenin zegt zolang er een keuze openstaat.
 *
 * De les van de landen-vraag: aanwijzingen die alleen in de bedieningslaag staan ziet
 * een muisspeler nooit — die laag schuift pas in beeld met de Tab-toets. Dit is het éne
 * kanaal dat iedereen ziet (waar ook "De dobbelstenen rollen…" woont), dus hier staat
 * bij elke vraag wat er van je verwacht wordt — en wat de ander aan het doen is. De
 * vragen met een eigen venster (browser, dialoog, plaatsingspaneel) vertellen het zelf.
 */
function aanwijzing(): string | undefined {
  if (afspelen !== undefined) return undefined;
  const vraag = view.pending;
  if (vraag === undefined) return undefined;

  if (vraag.seat !== JIJ) {
    if (vraag.kind === 'kies-tegenkaart') return `${TEGENSTANDER_NAAM} overweegt het verweer…`;
    if (vraag.kind === 'leg-landschap') return `${TEGENSTANDER_NAAM} legt zijn nieuwe landen neer…`;
    if (vraag.kind === 'kies-buit') return `${TEGENSTANDER_NAAM} kiest zijn buit…`;
    return `${TEGENSTANDER_NAAM} maakt een keuze…`;
  }

  switch (vraag.kind) {
    case 'kies-opbrengst':
      return vraag.reason === 'goed-jaar'
        ? 'Goed jaar: klik het landschap dat er een grondstof bij krijgt.'
        : 'Riddertoernooi gewonnen: klik het landschap dat er een grondstof bij krijgt.';
    case 'kies-buit':
      return `Pak je buit: klik een gevuld landschap van ${TEGENSTANDER_NAAM}${vraag.resterend > 1 ? ` (nog ${vraag.resterend})` : ''}.`;
    case 'plaats-buit':
      return `Leg je buit neer: klik je eigen landschap voor ${vraag.resource}.`;
    case 'handelaar':
      return `Handelaar: pak nog ${vraag.resterend} grondstof${vraag.resterend === 1 ? '' : 'fen'} bij ${TEGENSTANDER_NAAM}${mag('kies-klaar') ? ' — of klik Volgende: zo is het genoeg' : ''}.`;
    case 'handelaar-terug':
      return `Geef er één terug: klik de eigen grondstof die naar ${TEGENSTANDER_NAAM} gaat.`;
    case 'karavaan':
      return vraag.stap === 'geef'
        ? `Karavaan: klik ${vraag.resterend === 2 ? 'twee eigen grondstoffen' : 'nog één eigen grondstof'} om af te geven.`
        : `Karavaan: klik ${vraag.resterend === 2 ? 'twee eigen landschappen' : 'nog één eigen landschap'} voor je nieuwe grondstoffen.`;
    case 'kies-terugname':
      return vraag.soort === 'gebouw'
        ? `Wijs het gebouw van ${TEGENSTANDER_NAAM} aan dat hij terugneemt.`
        : `Wijs de ridder of vloot van ${TEGENSTANDER_NAAM} aan die hij terugneemt.`;
    case 'kies-afleggen':
      return 'Te veel kaarten: klik de handkaart die onder een stapel gaat.';
    case 'kies-afleg-stapel':
      return 'Klik links de stapel aan waar de kaart onder verdwijnt.';
    default:
      // De overige vragen openen een eigen venster dat de uitleg al draagt.
      return undefined;
  }
}

/**
 * Houdt het scherm wakker zolang er een potje op tafel ligt.
 *
 * Op een iPad valt het scherm anders in slaap terwijl je op de beurt van de ander wacht —
 * en een vergrendelde iPad pollt niet, dus je miste dan ook nog eens zijn hele beurt.
 * Alles hier is voor de gelegenheid: niet elke browser geeft het slot (spaarstand mag
 * weigeren), en dat is geen fout. Het slot vervalt zodra het tabblad naar de achtergrond
 * gaat; bij terugkeer vraagt de eerstvolgende tekenbeurt hem gewoon opnieuw aan.
 */
let wakker: WakeLockSentinel | undefined;
function houdSchermWakker(): void {
  if (wakker !== undefined) return;
  const slot = navigator.wakeLock;
  if (slot === undefined) return;
  slot
    .request('screen')
    .then((s) => {
      wakker = s;
      s.addEventListener('release', () => {
        wakker = undefined;
      });
    })
    .catch(() => {
      // Geweigerd (spaarstand, oude browser): het spel speelt gewoon, alleen dooft het
      // scherm dan op de gewone termijn.
    });
}

function render(): void {
  // Het menu en het opzetscherm dekken alles af; het bord eronder loopt gewoon door.
  tekenScherm();
  if (scherm !== 'spel') {
    stopToernooi();
    return;
  }

  houdSchermWakker();
  ververs();
  toernooiKlank();

  // De omlijsting op het canvas, op de plekken van het origineel.
  // "Speelbaar" is meer dan een actiekaart: het merendeel van het deck bestaat uit
  // uitbouwkaarten, en die bouw je in je vorstendom. Zonder ze hier mee te tellen bleven
  // ze in de hand hangen zonder dat het scherm iets aanbood.
  const zetten = view.legalActions;
  const speelbaar = new Set(
    zetten
      .filter((a) => a.type === 'speel-actiekaart' || a.type === 'bouw-uitbreiding')
      .map((a) =>
        a.type === 'speel-actiekaart' || a.type === 'bouw-uitbreiding' ? a.cardId : 0,
      ),
  );
  // Te veel kaarten? Dan opent je voorraad vanzelf met de stand erbij en knipperen de
  // kaarten die weg mogen — precies wat het origineel doet.
  const teVol = view.pending?.kind === 'kies-afleggen' && view.pending.seat === JIJ;
  hud.zetAfleggen(
    teVol ? `${view.you.hand.length}/${handLimitOf(view.you)}` : undefined,
  );
  // Wil je een kaart ruilen, dan hoort je voorraad open te staan zodat je kunt kiezen.
  if (modus.kind === 'ruil-handkaart') hud.zetAfleggen('kies een kaart om te ruilen');
  // De stapels moeten aanklikbaar zijn zodra je er een aanwijst — bij het afleggen én bij
  // de tweede stap van het ruilen — ook al mag je er op dat moment niets uit pakken.
  hud.zetKiestStapel(
    modus.kind === 'ruil-kies-stapel' ||
      (view.pending?.kind === 'kies-afleg-stapel' && view.pending.seat === JIJ),
  );
  // Ruilen mag de hele hoofdfase; het origineel laat de handenknop dan ook gewoon staan.
  hud.zetRuilbaar(
    view.turn === JIJ &&
      view.phase === 'hoofdfase' &&
      view.pending === undefined &&
      view.winner === undefined,
  );
  hud.zetBetaalbaar(betaalbareBouwsels());
  // Pakken van een uitbouwstapel kan alleen in de aanvulfase van jouw beurt; kan het
  // niet, dan staan de stapels leeg — precies zoals het origineel ze dan toont.
  // Tijdens de Bouwmeester kies je een stapel: dan moeten ze aanklikbaar zijn, ook al mag je
  // er buiten die gebeurtenis niets uit pakken. Dat gat maakte hem onspeelbaar — juist dan
  // waren de stapels onzichtbaar.
  const bouwmeesterKiestStapel =
    view.pending?.kind === 'bouwmeester' &&
    view.pending.seat === JIJ &&
    view.pending.stap === 'stapel';
  hud.zetStapelsBruikbaar(
    view.legalActions.some((a) => a.type === 'pak-kaart') ||
      bouwmeesterKiestStapel ||
      (view.phase === 'opzet' && view.you.hand.length === 0),
  );
  const balkregel =
    performance.now() < worpStilTot
      ? 'De dobbelstenen rollen…'
      : (aanwijzing() ?? spel.verslag()[0] ?? '');
  hud.render(
    view,
    { jij: view.you.victoryPoints, tegen: view.opponent.victoryPoints },
    balkregel,
    view.you.hand.map((id) => {
      const kaart = cardById.get(id);
      return {
        id,
        naam: kaart?.name ?? `kaart ${id}`,
        speelbaar: speelbaar.has(id),
        ...(kaart?.cost === undefined ? {} : { kosten: kaart.cost }),
      };
    }),
  );

  // Daarnaast een verborgen HTML-laag met dezelfde mogelijkheden, zodat toetsenbord en
  // schermlezer blijven werken — het canvas is voor hen onzichtbaar.
  el('hand').innerHTML = handHtml(view);

  el('acties').innerHTML = actieknoppen(view);
  // De verslagregels gaan er als tékst in, niet als HTML. Vandaag zijn ze veilig — de
  // regelkern bouwt ze uit namen die door een schema komen — maar die belofte woont drie
  // pakketten verderop, en één nieuw veld met vrije tekst zou hier anders meteen een
  // gat zijn.
  const logLijst = el('log');
  logLijst.replaceChildren(
    ...spel.verslag().slice(0, 30).map((r) => {
      const li = document.createElement('li');
      li.textContent = r;
      return li;
    }),
  );

  // Het bord tekent uit de bevroren stand zolang een worp uitgespeeld wordt: anders
  // liggen de grondstoffen er al terwijl de dobbelsteen nog rolt. Alleen het bord — de
  // knoppen blijven op de levende stand, want een scherm dat een seconde per worp niet
  // reageert is erger dan een getal dat een seconde te vroeg klopt.
  board.render(bevrorenStand ?? view, highlightsVoor());

  // Het ruilpaneel: als jij ernaartoe gaat, én als de computer jou iets aanbiedt. Dat is
  // in het origineel hetzelfde scherm — alleen staat het voorstel er dan al in.
  //
  // Maar niet dwars door het afspelen heen: de computer biedt op de server meteen aan
  // het begin van zijn beurt, terwijl zijn worp hier nog moet rollen. Het aanbod hoort
  // pas ná de worp en de opbrengst — eerst zien wat er gebeurde, dan pas de vraag.
  // `leegDeRij` sluit af met `render()`, dus het paneel komt vanzelf zodra alles gespeeld
  // is.
  const aanbod = afspelen === undefined ? view.offer : undefined;
  // Staat het paneel open terwijl de voorraad ondertussen veranderde, dan tekenen we het
  // opnieuw. Anders bied je met getallen van voor de laatste worp.
  if (overlay?.soort === 'ruil' && ruilVoorraad !== voorraadStempel()) {
    sluitOverlay();
  }
  if (aanbod !== undefined && aanbod.from === COMPUTER) {
    if (overlay?.soort !== 'ruil') toonRuilpaneel(aanbod);
  } else if (modus.kind === 'ruil-kies-soort') {
    if (overlay?.soort !== 'ruil') toonRuilpaneel();
  } else if (overlay?.soort === 'ruil') {
    sluitOverlay();
  }

  // De opzet: het paneel dat je door het neerleggen en het kiezen van je startkaarten
  // praat. De vraag "wil je zelf verdelen?" stond hier ooit als losse dialoog; die hoort
  // bij het opzetscherm, vóór het potje bestaat — en bij een potje tussen twee mensen was
  // hij ronduit gevaarlijk: ja of nee zeggen maakte dan een níéuw potje, en je gedeelde
  // partij was stilletjes weg.
  if (view.phase === 'opzet' && (overlay === undefined || overlay.soort === 'opzet')) {
    toonVerdeelpaneel();
  }

  // Betaal je ergens voor, dan telt het paneel af wat er nog moet komen.
  if (modus.kind === 'betalen') {
    const wat = modus.doel;
    const naam = doelNaam(wat);
    const open = resterendAantal(view.you, modus.kosten, modus.betaald);
    sluitOverlay();
    const laag = betaalPaneel({
      titel: `${naam.charAt(0).toUpperCase()}${naam.slice(1)} — nog te betalen:`,
      ...(typeof wat === 'string'
        ? { beeld: { groep: 'bouwwerken', naam: wat } }
        : 'kaart' in wat
          ? { cardId: wat.kaart }
          : {}),
      // Bij vrije kosten staat er geen soort tegenover; dan telt alleen het aantal.
      resterend:
        modus.kosten.soort === 'vast'
          ? resterendeKosten(view.you, modus.kosten, modus.betaald)
          : new Map([['naar keuze', open]]),
      onAnnuleer: () => {
        sluitOverlay();
        modus = { kind: 'normaal' };
        render();
      },
    });
    overlay = { soort: 'plaatsen', laag };
    hud.vensters.addChild(laag);
  }

  // Wijs je een plek aan, dan hoort daar het paneel van het origineel bij: een miniatuur
  // van wat je neerzet met de aanwijzing ernaast en een Annuleren-knop.
  //
  // De derde bron is de vraag van een nieuw dorp (`leg-landschap`): zonder dit paneel
  // zag je wél twee lege vakken oplichten maar niet wélk land je aan het neerleggen was —
  // en dan is "kies boven of onder" een gok in plaats van een plan. Uit de tweede
  // livetest: "ik wil natuurlijk 2 ertsvelden naast elkaar als het kan."
  const legVraag =
    view.pending?.kind === 'leg-landschap' && view.pending.seat === JIJ
      ? view.pending
      : undefined;
  const legEerste = legVraag?.kaarten[0];
  const legTweede = legVraag?.kaarten[1];
  const landLabel = (kaart: { landscape: string; dieNumber: number }): string =>
    `${kaart.landscape} (${LANDSCAPE_RESOURCE[kaart.landscape as keyof typeof LANDSCAPE_RESOURCE]} bij een ${kaart.dieNumber})`;
  const plaatsen =
    // De landen-vraag gaat vóór een naijlende modus: na het bouwen van een dorp kon de
    // bouwplek-modus nog openstaan, en dan zag je "Waar moet gebouwd worden?" met een
    // dorpsplaatje terwijl de kern al om de landen vroeg — het paneel van de tweede
    // schermafdruk van Thijs. Een openstaande vraag van de kern is altijd de waarheid.
    legVraag !== undefined && legEerste !== undefined
      ? {
          tekst:
            legTweede !== undefined
              ? `Klik boven of onder voor je ${landLabel(legEerste)} — je ${landLabel(legTweede)} komt aan de andere kant.`
              : `De landenstapel is bijna op: nog één land. Klik boven of onder voor je ${landLabel(legEerste)}.`,
          beeld: { groep: 'landschappen', naam: legEerste.landscape },
          ...(legTweede === undefined
            ? {}
            : { beeldTwee: { groep: 'landschappen', naam: legTweede.landscape } }),
          knopSoort: 'ok' as const,
          knopLabel: 'Leg maar neer',
        }
      : modus.kind === 'betalen'
        ? undefined
        : modus.kind === 'kies-bouwplek'
          ? {
              tekst: 'Waar moet gebouwd worden? Klik op een oplichtend bouwveld.',
              beeld: { groep: 'bouwwerken', naam: modus.bouw },
            }
          : modus.kind === 'kies-uitbouwplek'
            ? { tekst: 'Waar moet deze kaart komen? Klik op een oplichtend bouwwerk.', cardId: modus.cardId }
            : undefined;
  // Eén stempel per paneelinhoud: stond er al een plaatsingspaneel open (bijvoorbeeld
  // "Waar moet gebouwd worden?") en verandert de vraag (het dorp staat er, nu de
  // landen), dan moet het paneel écht opnieuw — anders bleef de oude tekst met het
  // dorpsplaatje staan terwijl de kern al om de landen vroeg. Uit het zesde potje.
  if (plaatsen !== undefined) {
    if (overlay?.soort === 'plaatsen' && plaatsenStempel !== plaatsen.tekst) {
      sluitOverlay();
    }
    if (overlay?.soort !== 'plaatsen') {
      plaatsenStempel = plaatsen.tekst;
      sluitOverlay();
      const bijLeg = legVraag;
      const laag = plaatsingsPaneel({
        ...plaatsen,
        onAnnuleer: () => {
          sluitOverlay();
          // Bij het nieuwe dorp is de knop geen annuleren maar "maakt mij niet uit":
          // de eerste kaart gaat dan gewoon boven. De vraag móét beantwoord; wegklikken
          // zonder antwoord zou het paneel bij de volgende tekenbeurt terugbrengen.
          if (bijLeg !== undefined) {
            void doAction({ type: 'kies', column: bijLeg.column, side: 'boven' });
            return;
          }
          modus = { kind: 'normaal' };
          render();
        },
      });
      overlay = { soort: 'plaatsen', laag };
      hud.vensters.addChild(laag);
    }
  } else if (overlay?.soort === 'plaatsen') {
    sluitOverlay();
  }

  // Elke beurt sluit af met een kaartvraag, zoals in het origineel: heb je je hand vol,
  // dan "Wil je een kaart ruilen?"; kom je kaarten tekort, dan "Bovenste kaart nemen?" of
  // "Nieuwe kaart kopen?". Pas als je hand op sterkte is, gaat de beurt over.
  if (
    view.phase === 'aanvullen' &&
    view.turn === JIJ &&
    view.pending === undefined &&
    modus.kind === 'normaal' &&
    overlay === undefined &&
    afspelen === undefined &&
    view.winner === undefined
  ) {
    toonKaartvraag();
  }

  // Heb je een stapel doorgekocht of kies je je startkaarten, dan schuift de browser open.
  const doorkijken = view.pending;
  const uitStapel =
    (doorkijken?.kind === 'kies-uit-stapel' || doorkijken?.kind === 'kies-startkaarten') &&
    doorkijken.seat === JIJ
      ? doorkijken
      : undefined;
  if (uitStapel !== undefined) {
    if (overlay?.soort !== 'browser') {
      toonBrowser(
        uitStapel.stack,
        uitStapel.kind === 'kies-startkaarten'
          ? `Kies je startkaarten (nog ${uitStapel.resterend})`
          : undefined,
      );
    }
  } else if (doorkijken?.kind === 'verkenner-kies' && doorkijken.seat === JIJ) {
    if (overlay?.soort !== 'browser') toonLandschapKeuze(doorkijken.zijde);
  } else if (doorkijken?.kind === 'spion' && doorkijken.seat === JIJ) {
    if (overlay?.soort !== 'browser') toonSpion(doorkijken.from);
  } else if (doorkijken?.kind === 'kies-handkaarten' && doorkijken.seat === JIJ) {
    if (overlay?.soort !== 'browser') toonConflict(doorkijken.from, doorkijken.aantal);
  } else if (
    doorkijken?.kind === 'bouwmeester' &&
    doorkijken.seat === JIJ &&
    doorkijken.stap === 'kaart'
  ) {
    // De kern zet `openStapel` klaar zodra je je stapel hebt gekozen; dezelfde browser als
    // bij een doorgekochte stapel.
    if (overlay?.soort !== 'browser') {
      toonBrowser(doorkijken.stack ?? 0, 'Bouwmeester — kies een kaart uit de stapel');
    }
  } else if (
    doorkijken?.kind === 'bouwmeester' &&
    doorkijken.seat === JIJ &&
    doorkijken.stap === 'afgeven'
  ) {
    if (overlay?.soort !== 'browser') toonBouwmeesterAfgeven();
  } else if (overlay?.soort === 'browser') {
    sluitOverlay();
  }

  // Aan het begin kies je eerst een stapel voor je drie startkaarten (handboek p. 11).
  if (
    view.phase === 'opzet' &&
    view.pending === undefined &&
    view.you.hand.length === 0 &&
    view.setupPool.length === 0 &&
    modus.kind === 'normaal' &&
    overlay === undefined
  ) {
    meld('Klik links een stapel aan om je drie startkaarten te kiezen.');
  }

  // Komt er een aanval op je af en heb je de tegenkaart, dan vraagt het spel of je hem
  // inzet — met een banner, zoals het origineel. De kaart gaat er niet vanzelf uit.
  const vraag = view.pending;
  if (vraag?.kind === 'kies-tegenkaart' && vraag.seat === JIJ && overlay === undefined) {
    const naam = cardById.get(vraag.counterId)?.name ?? 'je tegenkaart';
    toonDialoog(`${vraag.attack}! Wil je je ${naam} inzetten?`, [
      {
        soort: 'ja',
        onClick: () => {
          sluitOverlay();
          // Eerst de tegenkaart groot in beeld, dan pas de worp — die volgorde hoort erbij.
          const kaart = cardById.get(vraag.counterId);
          void kaartInBeeld(board.animaties, vraag.counterId, kaart?.rules ?? '');
          window.setTimeout(() => void doAction({ type: 'tegenkaart', inzetten: true }), 900);
        },
      },
      {
        soort: 'nee',
        onClick: () => {
          sluitOverlay();
          void doAction({ type: 'tegenkaart', inzetten: false });
        },
      },
    ]);
  }

  // Net een dorp gesticht met de Verkenner op zak? Dán is het moment om hem in te zetten
  // (handboek p. 46). Zeg je nee, dan komen de twee bovenste van de landschapstapel.
  if (vraag?.kind === 'verkenner-vraag' && vraag.seat === JIJ && overlay === undefined) {
    toonDialoog('Je nieuwe dorp krijgt twee landschappen. Zet je de Verkenner in?', [
      {
        soort: 'ja',
        onClick: () => {
          sluitOverlay();
          void doAction({ type: 'verkenner', inzetten: true });
        },
      },
      {
        soort: 'nee',
        onClick: () => {
          sluitOverlay();
          void doAction({ type: 'verkenner', inzetten: false });
        },
      },
    ]);
  }

  // Begint er een beurt, dan rolt de dobbelsteen vanzelf. Behalve zolang de eerste vraag
  // van het potje openstaat: wat er dan onder ligt is decor.
  if (!wachtOpAntwoord) dobbelVanzelf();

  // En is de computer aan zet zonder dat er een afspraak voor hem openstaat, dan maken we
  // er hier een. Vroeger hing dat alleen aan de staart van jóuw zet: bleef de computer
  // ergens zonder afspraak achter — na een aanbod, of na een zet die hij niet mocht
  // doen — dan stond het potje stil op "Even wachten…" zonder dat er iets kapot was.
  if (
    !wachtOpAntwoord &&
    view.winner === undefined &&
    view.offer?.from !== COMPUTER &&
    computerTimer === undefined &&
    (view.pending?.seat ?? view.turn) === COMPUTER
  ) {
    planComputer();
  }

  // De ander is al minuten weg: dat hoort óók op het bord te staan, niet alleen in de
  // (ingeschoven) bedieningslaag — een muisspeler zag de enige uitweg anders nooit.
  // Ook dwars door het opzetpaneel heen: wie tijdens de opzet op een verdwenen
  // tegenstander wacht, zit net zo goed gegijzeld als middenin het spel.
  if (
    spel.anderWeg?.() === true &&
    view.winner === undefined &&
    (overlay === undefined || overlay.soort === 'opzet') &&
    !opgeefBeantwoord
  ) {
    if (overlay !== undefined) sluitOverlay();
    const laag = dialoog({
      vraag: `${TEGENSTANDER_NAAM} is al een tijdje weg. Wil je het potje opgeven?`,
      knoppen: [
        {
          soort: 'ja',
          onClick: () => {
            opgeefBeantwoord = true;
            sluitOverlay();
            void (async () => {
              const mis = await spel.geefOp?.();
              if (mis !== undefined) {
                meld(mis);
                opgeefBeantwoord = false;
              }
              ververs();
              render();
            })();
          },
        },
        {
          soort: 'nee',
          onClick: () => {
            // Nog even wachten dan. De knop in de bedieningslaag blijft er voor wie zich
            // bedenkt.
            opgeefBeantwoord = true;
            sluitOverlay();
            render();
          },
        },
      ],
    });
    overlay = { soort: 'opgeven', laag };
    hud.vensters.addChild(laag);
    playSound('interface', UI.venster);
  }
  if (spel.anderWeg?.() !== true) opgeefBeantwoord = false;

  // Het potje bleek voorbij zonder winnaar: opgegeven, of niet meer te volgen. Zonder dit
  // blok bleef je naar een stilstaand bord kijken — geen eindscherm (er is geen winnaar),
  // geen knop, geen uitleg.
  const potjeStand = spel.status?.();
  if (
    (potjeStand === 'abandoned' || potjeStand === 'onbereikbaar') &&
    view.winner === undefined &&
    overlay?.soort !== 'einde'
  ) {
    if (overlay !== undefined) sluitOverlay();
    modus = { kind: 'normaal' };
    toonEindeDialoog(
      potjeStand === 'abandoned'
        ? 'Dit potje is gestopt: een van de spelers heeft het opgegeven.'
        : 'Dit potje is niet meer te volgen. Ververs de pagina of begin een nieuw potje.',
      [
        {
          soort: 'ok',
          onClick: () => {
            naarHub();
          },
        },
      ],
    );
    return;
  }

  // Het potje is afgelopen: het origineel toont dan een schermvullende tekening met de
  // score erboven, geen dialoogje midden op het bord.
  //
  // Een openstaande overlay houdt het einde niet meer tegen. Dat deed hij wel: stond jij
  // te betalen terwijl de ander online won, dan bleef je eeuwig naar het betaalpaneel van
  // een afgelopen partij kijken — de betalen-modus zet elke render zijn eigen overlay
  // terug, dus "wachten tot hij weg is" duurde voor altijd.
  if (view.winner !== undefined && overlay?.soort !== 'einde') {
    if (overlay !== undefined) sluitOverlay();
    modus = { kind: 'normaal' };
    const online = spel.tegenMens?.() === true;
    const wint = view.winner === JIJ;
    const score = view.soloScore;
    const verschil = view.you.victoryPoints - view.opponent.victoryPoints;
    const laag = eindScherm({
      gewonnen: wint,
      puntenVerschil: verschil,
      // De soloscore is een maat voor oefenpotjes; tussen twee mensen zegt hij niets.
      tekst: online
        ? wint
          ? 'Je hebt gewonnen!'
          : `${TEGENSTANDER_NAAM} heeft gewonnen.`
        : wint
          ? `Je hebt gewonnen! Je haalde ${score} punten.`
          : `${TEGENSTANDER_NAAM} heeft gewonnen. Je haalde ${score} punten.`,
      onVerder: () => {
        eindMuziek?.(600);
        eindMuziek = undefined;
        naarHub();
      },
    });
    overlay = { soort: 'einde', laag };
    hud.vensters.addChild(laag);
    // Tekening N hoort bij `END_(N-1)`, en daar ligt `ENDFX_(N-1)` onder — gemeten aan
    // het origineel: in twee potjes begonnen die twee op dezelfde seconde. De korte
    // melding vertelt de uitslag, het lange stuk draagt het scherm. (`ENDFX_008` gaat er
    // in het origineel nog aan vooraf, tijdens het optellen van de score; wij tonen het
    // eindscherm meteen, dus die aanloop slaan we over.)
    const nummer = String(eindTekening(wint, verschil) - 1).padStart(3, '0');
    // Wat er nog van een kaart of gebeurtenis speelde maakt plaats: het eindscherm draagt
    // zijn eigen muziek en die hoort niet onder een ander stuk te beginnen.
    stopVerhaal();
    playSound('eindspel', `END_${nummer}`);
    eindMuziek?.(600);
    eindMuziek = speelEnStop('eindspel-effecten', `ENDFX_${nummer}`);
  }

  // Tijdens de opzet richt je je eigen vorstendom in; daar hoort de camera dan ook te
  // staan, ook als de computer straks begint.
  const kijkNaarHen =
    view.phase === 'opzet'
      ? false
      : view.pending === undefined
        ? !view.yourTurn
        : view.pending.seat === COMPUTER;
  board.kijkNaar(kijkNaarHen ? 'tegenstander' : 'jij');
}

function actieknoppen(view: GameView): string {
  const acties = view.legalActions;
  const knoppen: string[] = [];

  // Betaalt de speler op dit moment? Dan gaat dat voor alles.
  if (modus.kind === 'betalen') {
    const rest =
      modus.kosten.soort === 'vast'
        ? resterendeKosten(view.you, modus.kosten, modus.betaald)
        : new Map([
            ['grondstoffen naar keuze', resterendAantal(view.you, modus.kosten, modus.betaald)],
          ]);
    const lijst = [...rest.entries()]
      .filter(([, n]) => n > 0)
      .map(([soort, n]) => `<li>${n}× ${soort}</li>`)
      .join('');
    return `
      <p class="wacht">Klik op je landschappen om ${doelNaam(modus.doel)} te betalen.</p>
      <ul class="kosten">${lijst || '<li>alles betaald…</li>'}</ul>
      <button data-annuleer="1">Annuleren</button>`;
  }
  if (modus.kind === 'ruil-kies-doel') {
    return `
      <p class="wacht">Klik het landschap dat de nieuwe grondstof krijgt.</p>
      <p class="grijs">Je geeft ${modus.give.length}× ${modus.soort} weg.</p>
      <button data-annuleer="1">Annuleren</button>`;
  }
  if (modus.kind === 'ruil-kies-soort') {
    const mogelijk = tradableWithBank(view.you, tradeAbilitiesOf(view.you));
    if (mogelijk.length === 0) {
      // Het ruilmenu staat wél open — ruilen met je tegenstander kan altijd. Alleen de
      // bank wil nu niets van je hebben.
      return `<p class="wacht">Het ruilmenu staat open. Ruilen met ${TEGENSTANDER_NAAM} doe je
              in het scherm zelf; de bank wil nu niets van je hebben.</p>
              <button data-annuleer="1">Terug</button>`;
    }
    const opties = mogelijk
      .map(
        (m) =>
          `<button data-ruil="${m.resource}">
             <img class="klein" src="${asset('symbolen', m.resource).src}" alt="" />
             ${m.rate}× ${m.resource} → 1 naar keuze
           </button>`,
      )
      .join('');
    return `<p class="wacht">Het ruilmenu staat open. Bij de bank kun je nu:</p>${opties}
            <button data-annuleer="1">Terug</button>`;
  }

  if (view.winner !== undefined) {
    const wie = view.winner === JIJ ? 'Jij wint! 🎉' : `${TEGENSTANDER_NAAM} wint.`;
    knoppen.push(`<p class="uitslag">${wie}</p>`);
  }
  // Ligt er een aanbod van de computer, dan wacht het potje op jouw antwoord. Op het
  // canvas staat daar het ruilscherm voor; wie met het toetsenbord speelt had hier
  // helemaal niets — en kwam dus niet verder. Tijdens het afspelen wachten ook deze
  // knoppen: zelfde reden als het paneel, eerst de worp, dan de vraag.
  if (view.offer?.from === COMPUTER && afspelen === undefined) {
    const geeft = beschrijfBundel(view.offer.give);
    const wil = beschrijfBundel(view.offer.want);
    knoppen.push(
      `<p class="wacht">${TEGENSTANDER_NAAM} biedt ${geeft} voor ${wil}.</p>
       <button data-aanbod="ja">Aanbod aannemen</button>
       <button data-aanbod="nee">Aanbod afwijzen</button>`,
    );
    return knoppen.join('');
  }
  // De verkennersvraag hoort ook hier te staan, anders komt wie met het toetsenbord
  // speelt niet verder: zolang die vraag openstaat kan er niets anders.
  if (view.pending?.kind === 'verkenner-vraag' && view.pending.seat === JIJ) {
    knoppen.push(
      `<p class="wacht">Je nieuwe dorp krijgt twee landschappen. Zet je de Verkenner in?</p>
       <button data-verkenner="ja">Verkenner inzetten</button>
       <button data-verkenner="nee">Van de stapel pakken</button>`,
    );
    return knoppen.join('');
  }
  if (view.pending?.seat === JIJ) {
    if (view.pending.kind === 'kies-afleggen') {
      knoppen.push(
        '<p class="wacht">Je hebt te veel kaarten. Kies er één om onder een stapel te leggen.</p>',
      );
    } else if (view.pending.kind === 'verkenner-kies') {
      const zijde = view.pending.zijde === 'boven' ? 'bovenaan' : 'onderaan';
      knoppen.push(
        `<p class="wacht">Verkenner: kies uit de stapel het landschap voor ${zijde}.</p>`,
      );
    } else if (view.pending.kind === 'spion') {
      knoppen.push(`<p class="wacht">Kies een kaart uit de hand van ${TEGENSTANDER_NAAM}.</p>`);
    } else if (view.pending.kind === 'handelaar') {
      const nog = view.pending.resterend;
      knoppen.push(
        `<p class="wacht">Handelaar: pak nog ${nog} grondstof${nog === 1 ? '' : 'fen'} bij ${TEGENSTANDER_NAAM}.</p>`,
      );
      // "Neem 1 óf 2": na de eerste mag je het hierbij laten.
      if (view.legalActions.some((a) => a.type === 'kies-klaar')) {
        knoppen.push('<button data-doe="kies-klaar">✔ Zo is het genoeg</button>');
      }
    } else if (view.pending.kind === 'handelaar-terug') {
      knoppen.push(
        `<p class="wacht">Handelaar: klik de eigen grondstof aan die je teruggeeft aan ${TEGENSTANDER_NAAM}.</p>`,
      );
    } else if (view.pending.kind === 'leg-landschap') {
      const eerste = view.pending.kaarten[0];
      knoppen.push(
        `<p class="wacht">Nieuw dorp: klik waar het land <strong>${eerste?.landscape ?? ''} ${eerste?.dieNumber ?? ''}</strong> komt — boven of onder.${view.pending.kaarten.length > 1 ? ' Het tweede land komt vanzelf aan de andere kant.' : ''}</p>`,
      );
    } else if (view.pending.kind === 'karavaan') {
      const nog = view.pending.resterend;
      knoppen.push(
        view.pending.stap === 'geef'
          ? `<p class="wacht">Karavaan: klik ${nog === 2 ? 'twee eigen grondstoffen' : 'nog één eigen grondstof'} aan om af te geven.</p>`
          : `<p class="wacht">Karavaan: klik ${nog === 2 ? 'twee landschappen' : 'nog één landschap'} aan om de nieuwe grondstoffen te leggen.</p>`,
      );
    } else {
      const waar = view.pending.kind === 'kies-buit' ? `bij ${TEGENSTANDER_NAAM}` : 'bij jezelf';
      knoppen.push(`<p class="wacht">Klik een landschap aan ${waar}.</p>`);
    }
  } else if (view.pending?.kind === 'kies-tegenkaart' && view.pending.seat !== JIJ) {
    // De aanvaller staart anders naar een stilgevallen bord terwijl de ander over zijn
    // Bisschop of Kruidenheks nadenkt — dat móment hoort zichtbaar te zijn.
    knoppen.push(`<p class="wacht">${TEGENSTANDER_NAAM} overweegt het verweer…</p>`);
  }
  if (acties.some((a) => a.type === 'gooi')) {
    knoppen.push('<button class="hoofd" data-doe="gooi">🎲 Gooien</button>');
  }
  for (const a of acties) {
    if (a.type !== 'pak-kaart') continue;
    knoppen.push(
      `<button data-stapel="${a.stack}">Kaart van stapel ${a.stack + 1} · ${view.stackSizes[a.stack]} over</button>`,
    );
  }
  // De ruilknop hoort de hele hoofdfase te staan, niet alleen als de bank toevallig al
  // wil: ruilen met je tegenstander kan altijd, en dat is juist de weg uit een tekort.
  if (view.phase === 'hoofdfase' && view.turn === JIJ && view.pending === undefined) {
    knoppen.push('<button data-ruilmenu="1">🤝 Ruilen</button>');
  }
  if (acties.some((a) => a.type === 'beurt-beeindigen')) {
    knoppen.push('<button class="einde" data-doe="beurt-beeindigen">Beurt beëindigen ➡</button>');
  }
  // De verbinding hapert of is weg: zeggen. Een stilte die stil blijft is precies wat
  // een speler niet kan onderscheiden van "de ander denkt lang na".
  const verbinding = spel.verbinding?.();
  if (verbinding === 'haperend') {
    knoppen.push('<p class="wacht">De verbinding hapert; we blijven het proberen…</p>');
  } else if (verbinding === 'weg') {
    knoppen.push(
      '<p class="wacht">Geen verbinding met de server. Zodra die terug is, gaat het potje verder.</p>',
    );
  }

  // Is de ander al minuten weg, dan mag je eruit. Bewust pas dán, en bewust geen gewone
  // opgeefknop: die zou een uitweg zijn uit een partij die je aan het verliezen bent.
  if (spel.anderWeg?.() === true) {
    knoppen.push(
      `<p class="wacht">${TEGENSTANDER_NAAM} is al een tijdje weg.</p>`,
      '<button data-opgeven="1">Potje opgeven</button>',
    );
  }
  if (knoppen.length === 0) knoppen.push('<p class="grijs">Even wachten…</p>');
  return knoppen.join('');
}

// ---------------------------------------------------------------- bediening

/** Start het bouwen: vanaf hier wijs je zelf aan waarmee je betaalt. */
/**
 * Welke bouwstapels kun je nu betalen?
 *
 * Alleen de grondstoffen tellen hier, niet of er ook plek voor is: het origineel kleurt
 * de stapel op wat je in kas hebt. Waar het ding mag komen te staan, zie je pas daarna
 * aan de plekken die oplichten.
 */
function betaalbareBouwsels(): ReadonlySet<Bouwsoort> {
  const speler = view.you;
  const uit = new Set<Bouwsoort>();
  if (!view0IsJouwBeurt()) return uit;

  // Niet alleen: heb ik de grondstoffen — ook: is er een plek. Een dorp zonder vrije
  // straat kun je niet zetten, en dan hoort de stapel grijs te blijven ook al kun je het
  // betalen.
  const plekken: Readonly<Record<'straat' | 'dorp' | 'stad', number>> = {
    straat: roadSpots(speler.principality).length,
    dorp: settlementSpots(speler.principality).length,
    stad: speler.principality.buildings.filter((b) => b.kind === 'dorp').length,
  };

  for (const [soort, naam] of [
    ['straat', 'Straat'],
    ['dorp', 'Dorp'],
    ['stad', 'Stad'],
  ] as const) {
    const kaart = BASIC_CARDS.find((c) => c.name === naam);
    if (kaart !== undefined && canAfford(speler, kaart.cost) && plekken[soort] > 0) {
      uit.add(soort);
    }
  }
  return uit;
}

/** Mag je nu überhaupt bouwen? */
function view0IsJouwBeurt(): boolean {
  return (
    view.turn === JIJ && view.phase === 'hoofdfase' && view.pending === undefined
  );
}

/**
 * Zet het betaalde bouwwerk neer op de aangewezen plek.
 *
 * De grondstoffen zijn dan al afgerekend; deze stap voert de zet uit met precies die
 * betaling.
 */
async function plaatsBouwwerk(
  kind: 'straat' | 'dorp' | 'stad',
  column: number,
): Promise<void> {
  if (modus.kind !== 'kies-bouwplek' || modus.bouw !== kind) return;
  const zet = { type: `bouw-${kind}`, column, payment: modus.payment } as Action;
  const bouwmodus = modus;
  modus = { kind: 'normaal' };
  if (!(await doAction(zet))) {
    // Mocht het niet, dan blijf je aanwijzen — je hebt net betaald en dat moet je niet
    // opnieuw hoeven doen omdat je één vakje ernaast klikte.
    modus = bouwmodus;
    render();
    return;
  }
  // Neerzetten hoort te klinken en te flikkeren, net als in het origineel — anders
  // verschijnt er alleen ineens iets op het bord.
  bouwFeedback(JIJ, column, 'midden');
}

/**
 * Wat je hoort en ziet als er iets wordt neergezet.
 *
 * Het bouwgeluid uit de cd plus de sterrenflits op de plek zelf. Zonder dat verschijnt er
 * alleen ineens iets op het bord en voelt bouwen als niets.
 */

/**
 * Laat zien dát er iets neergezet is: een flits op de plek.
 *
 * Hier klonk `FX_004` bij — het stuk van vijftien seconden dat bij het krijgen van de
 * riddermacht hoort. Dat was de blunder uit kennisbank 06: muziek als effectje, over
 * élke straat, elk dorp en elke uitbouwkaart heen. De klank komt nu uit `klankBij`, waar
 * hij hoort: `FX_010` voor een vloot, `FX_011` voor de rest.
 */
function bouwFeedback(seat: Seat, column: number, side: 'boven' | 'midden' | 'onder'): void {
  const plek = board.schermPositie(seat, column, side);
  board.flits(plek.x, plek.y);
}

/** Begin met bouwen: eerst afrekenen, daarna pas een plek aanwijzen. */
/**
 * Waarom je dit bouwwerk nú niet kunt bouwen — of `undefined` als het wel kan.
 *
 * Uit de eerste live test: met vijf dorpen op het bord kon je een zesde betalen, en daarna
 * was er geen plek om hem neer te leggen. `beginBouw` controleerde namelijk niets en liet je
 * gewoon de betaalmodus in; de regelkern zei pas nee toen je al had afgerekend.
 *
 * De materiaallimiet is de officiële regel (handboek p. 14): vijf dorpen, zeven straten,
 * zeven steden. Een stad neemt een dorp ván het bord, dus wie vastzit op vijf dorpen kan er
 * met een stad weer een vrijmaken. Dat staat in de melding, want "je dorpen zijn op" zonder
 * uitweg is een doodlopende mededeling.
 */
function waaromNietBouwen(kind: 'straat' | 'dorp' | 'stad'): string | undefined {
  const mijn = view.you.principality;
  const voorraad = voorraadBouwwerken(mijn, view.opponent.principality, kind);
  if (kind === 'straat') {
    if (voorraad === 0) return 'De straten zijn op — de voorraad is met jullie twee gedeeld.';
    if (roadSpots(mijn).length === 0) return 'Er is geen plek voor een straat.';
    return undefined;
  }
  if (kind === 'dorp') {
    if (voorraad === 0) {
      return 'De dorpen zijn op — bouw een stad, dan komt er weer een in de voorraad.';
    }
    if (settlementSpots(mijn).length === 0) {
      return 'Er is geen plek voor een dorp — bouw eerst een straat.';
    }
    return undefined;
  }
  if (voorraad === 0) return 'De steden zijn op — de voorraad is met jullie twee gedeeld.';
  if (!mijn.buildings.some((b) => b.kind === 'dorp')) {
    return 'Een stad bouw je op een dorp, en je hebt er geen.';
  }
  return undefined;
}

function beginBouw(kind: 'straat' | 'dorp' | 'stad'): void {
  const naam = kind === 'straat' ? 'Straat' : kind === 'dorp' ? 'Dorp' : 'Stad';
  const kaart = BASIC_CARDS.find((c) => c.name === naam);
  if (kaart === undefined) return;
  // Eerst kijken of het überhaupt kán. Anders betaal je voor iets dat nergens heen kan, en
  // dat merkte je pas als je al had afgerekend.
  const bezwaar = waaromNietBouwen(kind);
  if (bezwaar !== undefined) {
    weiger(bezwaar);
    return;
  }
  modus = { kind: 'betalen', doel: kind, kosten: vast(kaart.cost), betaald: [] };
  sluitOverlay();
  meld(`Betaal voor je ${naam.toLowerCase()}: klik de grondstoffen aan.`);
  render();
}

/**
 * Een handkaart aanklikken: actiekaarten spelen zich, uitbouwkaarten wil je bouwen.
 *
 * Dit staat apart omdat het van twee kanten komt: van de kaartenwaaier op het bord en van
 * de knoppen in de bedieningslaag. Die tweede kende alleen actiekaarten, dus wie met het
 * toetsenbord speelt kon geen ridder of vloot neerzetten — terwijl de knop wél als
 * speelbaar was gemarkeerd.
 */
function speelHandkaart(cardId: number): void {
  const mogelijk = view.legalActions;
  // Een kaart die een worpgetal wil (de Alchimist) staat er zés keer in, één per worp.
  // De `find()` hieronder zou blind de eerste pakken — altijd worp 1 — dus die kaart
  // krijgt eerst de vlakkenkiezer.
  if (
    mogelijk.some(
      (a) => a.type === 'speel-actiekaart' && a.cardId === cardId && a.target?.kind === 'worp',
    )
  ) {
    hud.sluitHand();
    toonAlchimistWorp();
    return;
  }
  const actie = mogelijk.find((a) => a.type === 'speel-actiekaart' && a.cardId === cardId);
  if (actie !== undefined) {
    hud.sluitHand();
    void doAction(actie);
    return;
  }
  // Een uitbouwkaart wordt niet "gespeeld" maar gebóuwd: je wijst eerst aan bij welk
  // bouwwerk hij komt te staan.
  if (mogelijk.some((a) => a.type === 'bouw-uitbreiding' && a.cardId === cardId)) {
    hud.sluitHand();
    const kaart = cardById.get(cardId);
    modus = {
      kind: 'betalen',
      doel: { kaart: cardId },
      kosten: vast(kaart?.cost ?? {}),
      betaald: [],
    };
    meld(`Betaal voor je ${kaart?.name ?? 'kaart'}: klik de grondstoffen aan.`);
    render();
    return;
  }
  weiger('Die kaart kun je nu niet spelen.');
}

/**
 * Zet de gekozen uitbouwkaart bij een bouwwerk neer.
 *
 * Er zijn twee zijden per bouwwerk; we nemen de eerste die mag. Betaald wordt met de
 * landschappen die jij hebt aangewezen — eerder pakte hij de zet uit de lijst van de
 * computer, en die kiest zijn eigen tegels. Dan liep dus een ander landschap leeg dan
 * dat je had aangeklikt, terwijl juist dát de keuze is die het spel je geeft.
 */
function plaatsUitbouw(column: number, zijde?: Side): void {
  if (modus.kind !== 'kies-uitbouwplek') return;
  const cardId = modus.cardId;
  const betaling = modus.payment;
  // Klik je een vrij vak aan, dan geldt díé kant; klik je het bouwwerk zelf aan, dan
  // pakken we de eerste kant die mag.
  const kanten: readonly Side[] = zijde === undefined ? ['boven', 'onder'] : [zijde];
  const zet = kanten
    .map(
      (side): Action => ({ type: 'bouw-uitbreiding', cardId, column, side, payment: betaling }),
    )
    .find((a) => a.type === 'bouw-uitbreiding' && mag('bouw-uitbreiding', {
      cardId: a.cardId,
      column: a.column,
      side: a.side,
    }));
  if (zet === undefined) {
    weiger('Daar kan deze kaart niet bij.');
    return;
  }
  modus = { kind: 'normaal' };
  void doAction(zet);
  bouwFeedback(JIJ, column, 'midden');
}

/** Verwerkt een klik op een eigen landschap tijdens het betalen. */
function betaalKlik(column: number, side: Side): void {
  if (modus.kind !== 'betalen') return;
  const speler = view.you;
  const mag = magAanwijzen(speler, modus.kosten, modus.betaald, { column, side });
  if (!mag.ok) {
    weiger(`Dat kan niet: ${mag.reden}.`);
    return;
  }

  modus = { ...modus, betaald: [...modus.betaald, { column, side }] };

  // Je ziet het schaap opstijgen vanaf de tegel die je aanwees, en pas daarna daalt de
  // voorraad. Dat is het verschil tussen "het getal veranderde" en "ik heb betaald".
  const tegel = speler.principality.landscapes.find((t) => t.column === column && t.side === side);
  if (tegel !== undefined) {
    const plek = board.schermPositie(JIJ, column, side);
    void grondstofWegvliegen(board.animaties, LANDSCAPE_RESOURCE[tegel.landscape], plek.x, plek.y);
  }

  // Betaald? Dan pas gebeurt er iets. Wát er gebeurt hangt van het doel af; de weg
  // ernaartoe is voor alle vier hetzelfde.
  if (volledigBetaald(speler, modus.kosten, modus.betaald)) {
    const betaling = modus.betaald;
    const wat = modus.doel;
    if (typeof wat === 'string') {
      modus = { kind: 'kies-bouwplek', bouw: wat, payment: betaling };
      meld('Betaald. Klik nu de plek aan.');
    } else if ('kaart' in wat) {
      modus = { kind: 'kies-uitbouwplek', cardId: wat.kaart, payment: betaling };
      meld('Betaald. Klik nu de plek aan.');
    } else if ('stapel' in wat) {
      // Nu pas gaat de stapel open. Hiervoor koos het scherm zelf twee grondstoffen en
      // lag de hele stapel open te kijken zonder dat je iets betaald had.
      modus = { kind: 'normaal' };
      void doAction({ type: 'koop-kaart', stack: wat.stapel, payment: betaling });
    } else if (wat.krijg === undefined) {
      modus = { kind: 'ruil-kies-doel', give: betaling, soort: wat.bank };
      meld('Betaald. Klik het landschap aan dat de nieuwe grondstof krijgt.');
    } else {
      // Uit het ruilpaneel: daar stond al wát je terugwilt, dus zoeken we er zelf een
      // landschap bij met ruimte.
      const krijg = wat.krijg;
      const doel = speler.principality.landscapes.find(
        (t) => LANDSCAPE_RESOURCE[t.landscape] === krijg && t.resources < 3,
      );
      modus = { kind: 'normaal' };
      if (doel === undefined) meld(`Je hebt geen ${krijg}-landschap met ruimte.`);
      else {
        void doAction({
          type: 'ruil-bank',
          give: betaling,
          receive: { column: doel.column, side: doel.side },
        });
      }
    }
  }
  render();
}

/** Ruilen met de bank: soort kiezen, dan het landschap dat de nieuwe grondstof krijgt. */
function ruilSoort(soort: Resource): void {
  const player = view.you;
  const koers = bankRate(soort, tradeAbilitiesOf(player));
  const heb = player.principality.landscapes
    .filter((t) => LANDSCAPE_RESOURCE[t.landscape] === soort)
    .reduce((som, t) => som + t.resources, 0);
  if (heb < koers) {
    meld(`Je hebt ${koers}× ${soort} nodig.`);
    return;
  }
  // Ook hier eerst afrekenen. Het scherm koos vroeger zelf welke drie hout eraf gingen,
  // en dan verdwenen ze zonder dat je ze zag of hoorde.
  modus = { kind: 'betalen', doel: { bank: soort }, kosten: vast({ [soort]: koers }), betaald: [] };
  sluitOverlay();
  meld(`Betaal ${koers}× ${soort} aan de bank: klik de grondstoffen aan.`);
  render();
}

async function ruilDoelKlik(column: number, side: Side): Promise<void> {
  if (modus.kind !== 'ruil-kies-doel') return;
  const zet: Action = { type: 'ruil-bank', give: modus.give, receive: { column, side } };
  const ruilmodus = modus;
  modus = { kind: 'normaal' };
  if (!(await doAction(zet))) {
    modus = ruilmodus;
    render();
  }
}

async function main(): Promise<void> {
  board = new Board({
    onLandscapeClick: (seat, column, side) => {
      // Eerst de schermmodi: betalen en ruilen gaan voor een spelkeuze.
      if (modus.kind === 'betalen') {
        if (seat === JIJ) betaalKlik(column, side);
        return;
      }
      if (modus.kind === 'ruil-kies-doel') {
        if (seat === JIJ) void ruilDoelKlik(column, side);
        return;
      }
      // Bij de opzet leg je je landschappen neer: het gekozen landschap uit je voorraad
      // gaat naar de plek die je aanklikt.
      if (modus.kind === 'verdeel-landschappen') {
        if (seat !== JIJ) return;
        const voorraad = view.setupPool;
        if (voorraad.length === 0) {
          meld('Al je landschappen liggen. Klik op Klaar om te beginnen.');
          return;
        }
        const plek = modus.gekozen ?? 0;
        void doAction({ type: 'plaats-landschap', poolIndex: plek, column, side });
        modus = { kind: 'verdeel-landschappen' };
        return;
      }

      const pending = view.pending;
      if (pending?.seat !== JIJ) return;
      const bron: Seat =
        pending.kind === 'kies-buit' || pending.kind === 'handelaar' ? pending.from : JIJ;
      if (seat !== bron) return;
      void doAction({ type: 'kies', column, side });
    },
    onSpotClick: (kind, column) => void plaatsBouwwerk(kind, column),
    onBuildingClick: (column) => {
      if (modus.kind === 'kies-uitbouwplek') {
        plaatsUitbouw(column);
        return;
      }
      void plaatsBouwwerk('stad', column);
    },
    onExpansionSlotClick: (column, side) => plaatsUitbouw(column, side),
    // Burgeroorlog: je wijst een ridder of vloot bij je tegenstander aan die hij terug op
    // zijn hand moet nemen. Het bord laat alleen geldige doelen oplichten, dus wat hier
    // binnenkomt is er altijd één.
    onGebouwdeKaartClick: (column, side) => {
      const pending = view.pending;
      if (pending?.kind !== 'kies-terugname' || pending.seat !== JIJ) return;
      void doAction({ type: 'kies', column, side });
    },
  });
  await board.mount(el('veld'));

  hud = new Hud({
    onBouwstapelClick: (soort) => {
      if (soort === 'landschap') {
        weiger('Landschappen krijg je erbij zodra je een dorp bouwt — die koop je niet los.');
        return;
      }
      // Aanklikken begint met betalen; de plek wijs je daarna aan.
      beginBouw(soort);
    },
    onStapelClick: (index) => {
      // Bij de opzet kies je hier de stapel waaruit je je drie startkaarten pakt.
      if (view.phase === 'opzet' && view.you.hand.length === 0) {
        void doAction({ type: 'kies-startstapel', stack: index });
        return;
      }
      // Tweede stap van het ruilen: onder wélke stapel gaat hij? Daarna zit er ruimte in
      // je hand en komt de gewone aanvulvraag vanzelf terug — gratis de bovenste, of twee
      // grondstoffen betalen en de stapel doorzoeken (handboek p. 10).
      if (modus.kind === 'ruil-kies-stapel') {
        const plek = modus.handIndex;
        modus = { kind: 'normaal' };
        void doAction({ type: 'ruil-af', handIndex: plek, stack: index });
        return;
      }
      // Bouwmeester, eerste stap: uit welke stapel pak je?
      if (
        view.pending?.kind === 'bouwmeester' &&
        view.pending.seat === JIJ &&
        view.pending.stap === 'stapel'
      ) {
        if (index === view.pending.verboden) {
          weiger('Die stapel heeft je tegenstander al genomen.');
          return;
        }
        void doAction({ type: 'kies', column: index, side: 'boven' });
        return;
      }
      // Tweede stap van het afleggen: onder wélke stapel gaat de kaart?
      if (view.pending?.kind === 'kies-afleg-stapel' && view.pending.seat === JIJ) {
        hud.sluitHand();
        void doAction({ type: 'kies', column: index, side: 'boven' });
        return;
      }
      // Een stapel doorkopen: eerst afrekenen, dan pas kijken. De stapel gaat open zodra
      // de laatste grondstof is aangewezen — zie `betaalKlik`.
      if (modus.kind === 'koop-stapel') {
        const prijs = koopPrijsVoor(view.you);
        if (totaleVoorraad(view.you.principality.landscapes) < prijs) {
          meld(`Je hebt ${prijs} grondstoffen nodig om een stapel door te kijken.`);
          return;
        }
        modus = { kind: 'betalen', doel: { stapel: index }, kosten: vrij(prijs), betaald: [] };
        meld(`Betaal ${prijs} grondstoffen om stapel ${index + 1} door te kijken.`);
        render();
        return;
      }
      if (modus.kind === 'ruil-handkaart') {
        meld('Klik eerst een handkaart aan.');
        return;
      }
      if (modus.kind === 'pak-stapel') modus = { kind: 'normaal' };
      const zet: Action = { type: 'pak-kaart', stack: index };
      if (mag('pak-kaart', { stack: index })) void doAction(zet);
      else weiger('Van die stapel kun je nu geen kaart pakken.');
    },
    onHandClick: () => {
      playSound('interface', UI.handOpen);
      render();
    },
    // De twee handen aan de linkerrand openen het ruilmenu — de weg die het handboek
    // wijst (p. 14). De modus blijft staan zodat `render` het paneel open houdt.
    onRuilClick: () => {
      // Alleen de modus zetten; `render` opent het paneel. Zelf openen en daarna
      // hertekenen ging mis toen de HUD nog één laag was.
      modus = { kind: 'ruil-kies-soort' };
      render();
    },
    onKaartClick: (cardId) => {
      // Aan het eind van je beurt een kaart ruilen: deze gaat onder een stapel, en je
      // krijgt er een nieuwe voor terug.
      if (modus.kind === 'ruil-handkaart') {
        const plek = view.you.hand.indexOf(cardId);
        // Ruilen kan alleen aan het eind van je beurt. Tussen je klik en dit moment kan de
        // stand verschoven zijn — dan sturen we niets: een zet waarvan je wéét dat hij
        // geweigerd wordt, levert alleen een onbegrijpelijke melding op.
        if (plek < 0 || view.phase !== 'aanvullen') return;
        // Hier koos het scherm zélf de stapel: `findIndex(hoogte > 0)`, dus altijd de
        // eerste met kaarten erin. Je gaf een kaart weg en kreeg er blind een terug, zonder
        // te zien waaruit. In het echte potje gebeurde dat 72 keer — samen met
        // `beurt-beeindigen` de meest gespeelde zet. Nu wijs je zelf aan, precies zoals bij
        // het afleggen.
        modus = { kind: 'ruil-kies-stapel', handIndex: plek };
        hud.sluitHand();
        meld('Onder welke stapel gaat die kaart?');
        render();
        return;
      }

      // Staat de hand te vol, dan wijst een klik niet aan wat je speelt maar wat je
      // afleggen wilt (handboek msg 22). De hand blijft open: daarna wijs je nog aan
      // onder wélke stapel hij verdwijnt.
      if (view.pending?.kind === 'kies-afleggen' && view.pending.seat === JIJ) {
        const plek = view.you.hand.indexOf(cardId);
        if (plek < 0) return;
        void doAction({ type: 'kies', column: plek, side: 'boven' });
        meld('Klik nu links de stapel aan waar de kaart onder mag.');
        return;
      }
      void speelHandkaart(cardId);
    },
    onVolgende: () => {
      if (mag('beurt-beeindigen')) void doAction({ type: 'beurt-beeindigen' });
      else if (mag('gooi')) void doAction({ type: 'gooi' });
      // De Handelaar: "neem 1 of 2" — de volgende-knop is dan "zo is het genoeg".
      else if (mag('kies-klaar')) void doAction({ type: 'kies-klaar' });
    },
    onSluiten: () => {
      // Sluiten is "terug naar de hub", niet "gooi dit weg en begin iets nieuws". Het
      // potje staat op de server en wacht daar netjes onder "Verder spelen".
      naarHub();
    },
  });
  board.zetHud(hud.laag, hud.vensters);
  // Eén toelichtingskadertje voor het hele scherm; de HUD leent dat van het bord.
  hud.zetTooltips(board.tooltips);

  // Gebeurtenisdelegatie: de HTML wordt bij elke tekening vervangen.
  el('acties').addEventListener('click', (event) => {
    const knop = (event.target as HTMLElement).closest('button');
    if (knop === null) return;
    if (knop.dataset['opgeven'] !== undefined) {
      void (async () => {
        const mis = await spel.geefOp?.();
        if (mis !== undefined) {
          meld(mis);
        } else {
          // Het potje is voorbij; blijven kijken naar een bord dat niet meer verandert is
          // geen einde. Terug naar de hub, waar je iets nieuws kunt kiezen.
          naarHub();
          meld('Het potje is opgegeven omdat je tegenstander weg was.');
          return;
        }
        ververs();
        render();
      })();
      return;
    }
    if (knop.dataset['annuleer'] !== undefined) {
      modus = { kind: 'normaal' };
      render();
      return;
    }
    const aanbod = knop.dataset['aanbod'];
    if (aanbod !== undefined) {
      sluitOverlay();
      modus = { kind: 'normaal' };
      void doAction({ type: 'ruil-antwoord', accepteren: aanbod === 'ja' });
      return;
    }
    const verkenner = knop.dataset['verkenner'];
    if (verkenner !== undefined) {
      sluitOverlay();
      void doAction({ type: 'verkenner', inzetten: verkenner === 'ja' });
      return;
    }
    if (knop.dataset['ruilmenu'] !== undefined) {
      modus = { kind: 'ruil-kies-soort' };
      render();
      return;
    }
    const ruil = knop.dataset['ruil'];
    if (ruil !== undefined) {
      ruilSoort(ruil as Resource);
      return;
    }
    const doe = knop.dataset['doe'];
    if (doe === 'gooi' || doe === 'beurt-beeindigen' || doe === 'kies-klaar') {
      void doAction({ type: doe } as Action);
    }
    const stapel = knop.dataset['stapel'];
    if (stapel !== undefined) void doAction({ type: 'pak-kaart', stack: Number(stapel) });
  });

  el('hand').addEventListener('click', (event) => {
    const knop = (event.target as HTMLElement).closest('button');
    if (knop === null) return;
    void speelHandkaart(Number(knop.dataset['card']));
  });

  el('nieuw').addEventListener('click', () => {
    naarHub();
  });

  // Escape breekt betalen of ruilen af. Zie ook de bedieningslaag verderop: Escape sluit
  // die pas als je nergens middenin zit, anders zou één druk twee dingen doen — en dan
  // raak je je halve betaling kwijt terwijl je alleen een paneel wilde wegklikken.
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modus.kind !== 'normaal') {
      modus = { kind: 'normaal' };
      render();
    }
  });

  el('geluid').addEventListener('change', (event) => {
    setSoundEnabled((event.target as HTMLInputElement).checked);
  });

  el('volscherm').addEventListener('click', () => void volledigScherm());
  window.addEventListener('keydown', (event) => {
    if (event.key === 'F11') {
      event.preventDefault();
      void volledigScherm();
    }
  });

  // De bedieningslaag blijft open zodra hij focus krijgt, tot je hem sluit met Escape
  // of ergens op het speelveld klikt.
  const bediening = el('toegankelijk');
  /**
   * Sluit de laag echt.
   *
   * De klasse weghalen is niet genoeg: hij staat óók open zolang er focus in zit
   * (`:focus-within`). Dat viel lang niet op omdat elke tekenbeurt de knoppen vervangt en
   * de focus dus vanzelf kwijtraakte — een sluiting bij toeval. Zodra er één knop in kwam
   * die blíjft staan, bleef de laag openstaan en dekte hij de linkerkant van het bord af.
   */
  const sluitBediening = (): void => {
    bediening.classList.remove('open');
    const focus = document.activeElement;
    if (focus instanceof HTMLElement && bediening.contains(focus)) focus.blur();
  };
  bediening.addEventListener('focusin', () => bediening.classList.add('open'));
  el('veld').addEventListener('pointerdown', sluitBediening);
  window.addEventListener('keydown', (event) => {
    // Alleen als je nergens middenin zit; anders breekt Escape eerst dát af.
    if (event.key === 'Escape' && modus.kind === 'normaal') sluitBediening();
  });

  window.addEventListener('resize', () => board.herteken());
  render();

  // De toetshaak, alleen in een ontwikkelbouw. In een productiebouw snoeit Vite hem weg;
  // zie `toetshaak.ts` voor waarom dat streng moet.
  installeerToetshaak(() => spel, render, () => ({
    scherm,
    ingelogd: ik !== undefined,
    fase: view.phase,
    hand: view.you.hand.length,
    landschappen: view.you.principality.landscapes.length,
    bevroren: bevrorenStand !== undefined,
    /**
     * Hoeveel zetten van de ander er zijn afgespeeld — monotoon oplopend.
     *
     * Er staat al `bevroren`, maar daar kun je een browsertoets niet op bouwen: met
     * `reducedMotion: 'reduce'` duurt een animatie een paar honderd milliseconden en mis je
     * dat venster tussen twee peilingen door. Een teller die alleen omhoog gaat kun je niet
     * missen.
     */
    afgespeeld,
    /**
     * Welke twee figuren er in beeld staan.
     *
     * Online kreeg iedereen hetzelfde portret: het figuur van de ander werd nooit
     * meegestuurd, dus viel `portret()` terug op Louis. Met beide waarden hier kan een
     * twee-browser-toets vastleggen dat A en B elkaar goed zien — en dat A bij zichzelf iets
     * anders ziet dan B bij zichzelf.
     */
    figuur: spel.figuur?.(),
    tegenfiguur: spel.tegenfiguur?.(),
    ...(overlay === undefined ? {} : { overlay: overlay.soort }),
    modus: modus.kind,
  }), () => tijdlijnOpname);

  // De voordeur. Eerst kijken of er al een sessie is; zo ja, dan meteen door naar de
  // hub — zo nee, dan het inlogscherm. Er start hier met opzet níéts vanzelf: wie een
  // lopend potje heeft, vindt het in de hub onder "Verder spelen".
  tekenAccount();
  bewaardeCode = codeUitDeLink();
  void wieBenIkPreciezer().then((wie) => {
    geenServer = wie === 'geen-server';
    ik = geenServer ? undefined : (wie as typeof ik);
    tekenAccount();
    if (ik === undefined) {
      gaNaar('inlog');
      return;
    }
    naarHub();
  });
}

void main();
